import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import Select from "react-select";
import TableContainer from "../../components/Common/TableContainer";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { differenceInDays, parseISO, format } from "date-fns";
import { getEmployeeLeaves, saveEmployeeLeave, checkBookingConflicts } from "../../components/Api/GmsEmployeeLeave";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getLeaveTypes } from "../../components/Api/GmsLeaveType";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { processEmployeeCredits, getEmployeeCreditHistory } from "../../components/Api/GmsAnnualLeaveCreditHistory";
import { getLeaveBalanceHistory, saveLeaveBalanceHistory } from "../../components/Api/GmsLeaveBalanceHistory";
import { getCompensatoryLeaves, saveCompensatoryLeave } from "../../components/Api/GmsCompensatoryLeave";
import axios from "axios";
import BASE_URL from "../../config";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

const defaultForm = {
  Leave_Id: 0,
  Leave_Employee_Id: null,
  Leave_LeaveType_Id: null,
  Leave_RequestDate: new Date().toISOString(),
  Leave_FromDate: '',
  Leave_ToDate: '',
  Leave_RequestedDays: 0,
  Leave_Days: 0,
  Leave_Comments: '',
  Leave_RejoinDate: '',
  LeaveType_CreatedDate: new Date().toISOString(),
  LeaveType_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  Leave_ApprovedDate: new Date().toISOString(),
  Leave_ApprovedBy: Number(localStorage.getItem("userId")) || 1,
  Is_Cancelled: false,
  OffDays: 0,
  Is_HalfDay: false
};

const defaultCompensatoryForm = {
  Compensatory_Id: 0,
  Employee_Id: null,
  LeaveBalanceHistory_Id: null,
  CompensatoryDate: '',
  Comments: '',
  CreatedBy: Number(localStorage.getItem("userId")) || 1,
};

const emptyFilter = {
  Leave_Id: null,
  Leave_Employee_Id: null,
  Leave_LeaveType_Id: null,
  Leave_FromDate: null,
  Leave_ToDate: null,
  Leave_RequestDate: null,
  Leave_ApprovedDate: null,
};

// Utility function to calculate leave days
function calculateLeaveDaysUtil(fromDate, toDate, offDays = 0, isHalfDay = false) {
  if (!fromDate || !toDate) return { requestedDays: 0, leaveDays: 0 };
  const start = new Date(fromDate);
  const end = new Date(toDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  // Validate that end date is not before start date
  if (end < start) {
    console.warn('End date is before start date in calculateLeaveDaysUtil');
    return { requestedDays: 0, leaveDays: 0 };
  }

  // Use differenceInDays from date-fns for more reliable calculation
  const totalDays = differenceInDays(end, start) + 1;
  let requestedDays = totalDays;
  let leaveDays = Math.max(0, totalDays - (Number(offDays) || 0));
  if (isHalfDay) {
    if (totalDays === 1) {
      leaveDays = 0.5;
    } else {
      leaveDays = Math.max(0, leaveDays - 0.5); // Ensure leaveDays doesn't go negative
    }
  }
  leaveDays = Math.round(leaveDays * 100) / 100;
  requestedDays = Math.round(requestedDays * 100) / 100;
  return { requestedDays, leaveDays };
}

// Utility function to calculate days per year for cross-year leaves
function calculateDaysPerYear(fromDate, toDate, leaveTypeId = null) {
  if (!fromDate || !toDate) return { fromYear: 0, toYear: 0 };

  const start = new Date(fromDate);
  const end = new Date(toDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const fromYear = start.getFullYear();
  const toYear = end.getFullYear();

  if (fromYear === toYear) {
    // Single year leave
    const totalDays = differenceInDays(end, start) + 1;
    return { fromYear: totalDays, toYear: 0 };
  } else {
    // Cross-year leave
    // For Annual Leave (ID: 1) and Compensatory Leave (ID: 8), always use total days in fromYear
    const isCarryForwardLeave = leaveTypeId === 1 || leaveTypeId === 8;

    if (isCarryForwardLeave) {
      const totalDays = differenceInDays(end, start) + 1;
      return { fromYear: totalDays, toYear: 0 };
    } else {
      // For other leave types, split by year
      const yearEnd = new Date(fromYear, 11, 31); // December 31st of from year
      const yearStart = new Date(toYear, 0, 1);   // January 1st of to year

      const daysInFromYear = differenceInDays(yearEnd, start) + 1;
      const daysInToYear = differenceInDays(end, yearStart) + 1;

      return { fromYear: daysInFromYear, toYear: daysInToYear };
    }
  }
}

const validateLeaveBalance = async (employeeId, leaveTypeId, requestedDays, fromDate, toDate, existingLeaveId = null) => {
  try {
    // Skip balance validation for unpaid leave (type ID 9)
    if (leaveTypeId === 9) {
      return true;
    }

    // Skip balance validation for Annual Leave (type ID 1) at application time.
    // For Annual Leave, balance validation is deferred to rejoin time so that
    // employees can apply for future leave before their monthly credits accumulate.
    if (leaveTypeId === 1) {
      return true;
    }

    const currentYear = new Date().getFullYear();
    const fromYear = new Date(fromDate).getFullYear();
    const toYear = new Date(toDate).getFullYear();
    const isCrossYearLeave = fromYear !== toYear;

    // For Annual Leave (ID: 1) and Compensatory Leave (ID: 8), always use current year balance
    const isCarryForwardLeave = leaveTypeId === 1 || leaveTypeId === 8;

    if (isCrossYearLeave && !isCarryForwardLeave) {
      // Handle cross-year leave validation
      const { fromYear: daysInFromYear, toYear: daysInToYear } = calculateDaysPerYear(fromDate, toDate, leaveTypeId);

      // Get balances for both years
      const [balanceHistoryFromYear, balanceHistoryToYear] = await Promise.all([
        getLeaveBalanceHistory({
          Employee_Id: employeeId,
          BalanceYear: fromYear
        }),
        getLeaveBalanceHistory({
          Employee_Id: employeeId,
          BalanceYear: toYear
        })
      ]);

      const relevantBalanceFromYear = balanceHistoryFromYear.find(b => b.leaveType_Id === leaveTypeId);
      const relevantBalanceToYear = balanceHistoryToYear.find(b => b.leaveType_Id === leaveTypeId);

      if (!relevantBalanceFromYear || !relevantBalanceToYear) {
        throw new Error(`No balance found for leave type: ${leaveTypeId} in one or both years`);
      }

      // Get all leaves for both years
      const leavesResponse = await getEmployeeLeaves({
        Leave_Employee_Id: employeeId,
        Leave_LeaveType_Id: leaveTypeId
      });

      if (!leavesResponse.isSuccess) {
        throw new Error(leavesResponse.message || 'Failed to load leaves');
      }

      const allLeaves = leavesResponse.data || [];

      // Calculate used days for each year
      const usedDaysFromYear = allLeaves.reduce((total, leave) => {
        const leaveId = leave.Leave_Id || leave.leave_Id;
        const leaveFromYear = new Date(leave.leave_FromDate).getFullYear();
        if (!leave.is_Cancelled && !leave.Is_Cancelled &&
          parseInt(leaveId) !== parseInt(existingLeaveId) &&
          leaveFromYear === fromYear) {
          return total + (parseFloat(leave.leave_Days || leave.Leave_Days) || 0);
        }
        return total;
      }, 0);

      const usedDaysToYear = allLeaves.reduce((total, leave) => {
        const leaveId = leave.Leave_Id || leave.leave_Id;
        const leaveToYear = new Date(leave.leave_ToDate).getFullYear();
        if (!leave.is_Cancelled && !leave.Is_Cancelled &&
          parseInt(leaveId) !== parseInt(existingLeaveId) &&
          leaveToYear === toYear) {
          return total + (parseFloat(leave.leave_Days || leave.Leave_Days) || 0);
        }
        return total;
      }, 0);

      // Special handling for sick leave (type 2)
      const isSickLeave = leaveTypeId === 2;

      if (isSickLeave) {
        // Validate from year
        const totalFullPaidFrom = parseFloat(relevantBalanceFromYear.total_FullPaidDays) || 0;
        const totalHalfPaidFrom = parseFloat(relevantBalanceFromYear.total_HalfPaidDays) || 0;
        const totalUnpaidFrom = parseFloat(relevantBalanceFromYear.total_UnpaidDays) || 0;
        const totalCombinedBalanceFrom = totalFullPaidFrom + totalHalfPaidFrom + totalUnpaidFrom;
        const availableCombinedDaysFrom = totalCombinedBalanceFrom - usedDaysFromYear;

        // Validate to year
        const totalFullPaidTo = parseFloat(relevantBalanceToYear.total_FullPaidDays) || 0;
        const totalHalfPaidTo = parseFloat(relevantBalanceToYear.total_HalfPaidDays) || 0;
        const totalUnpaidTo = parseFloat(relevantBalanceToYear.total_UnpaidDays) || 0;
        const totalCombinedBalanceTo = totalFullPaidTo + totalHalfPaidTo + totalUnpaidTo;
        const availableCombinedDaysTo = totalCombinedBalanceTo - usedDaysToYear;

        const roundedDaysInFromYear = Math.round(daysInFromYear * 100) / 100;
        const roundedDaysInToYear = Math.round(daysInToYear * 100) / 100;
        const roundedAvailableFrom = Math.round(availableCombinedDaysFrom * 100) / 100;
        const roundedAvailableTo = Math.round(availableCombinedDaysTo * 100) / 100;

        if (roundedDaysInFromYear > roundedAvailableFrom) {
          throw new Error(`Insufficient sick leave balance in ${fromYear}. Available: ${roundedAvailableFrom.toFixed(2)} days, Requested: ${roundedDaysInFromYear.toFixed(2)} days`);
        }

        if (roundedDaysInToYear > roundedAvailableTo) {
          throw new Error(`Insufficient sick leave balance in ${toYear}. Available: ${roundedAvailableTo.toFixed(2)} days, Requested: ${roundedDaysInToYear.toFixed(2)} days`);
        }
      } else {
        // For other leave types
        const totalAvailableFrom = parseFloat(relevantBalanceFromYear.total_FullPaidDays) || 0;
        const availableDaysFrom = totalAvailableFrom - usedDaysFromYear;

        const totalAvailableTo = parseFloat(relevantBalanceToYear.total_FullPaidDays) || 0;
        const availableDaysTo = totalAvailableTo - usedDaysToYear;

        const roundedDaysInFromYear = Math.round(daysInFromYear * 100) / 100;
        const roundedDaysInToYear = Math.round(daysInToYear * 100) / 100;
        const roundedAvailableFrom = Math.round(availableDaysFrom * 100) / 100;
        const roundedAvailableTo = Math.round(availableDaysTo * 100) / 100;

        if (roundedDaysInFromYear > roundedAvailableFrom) {
          throw new Error(`Insufficient leave balance in ${fromYear}. Available: ${roundedAvailableFrom.toFixed(2)} days, Requested: ${roundedDaysInFromYear.toFixed(2)} days`);
        }

        if (roundedDaysInToYear > roundedAvailableTo) {
          throw new Error(`Insufficient leave balance in ${toYear}. Available: ${roundedAvailableTo.toFixed(2)} days, Requested: ${roundedDaysInToYear.toFixed(2)} days`);
        }
      }
    } else {
      // Original single-year logic (unchanged)
      const balanceYear = isCarryForwardLeave ? currentYear : fromYear;

      // Get balance history
      const balanceHistory = await getLeaveBalanceHistory({
        Employee_Id: employeeId,
        BalanceYear: balanceYear
      });

      const relevantBalance = balanceHistory.find(b => b.leaveType_Id === leaveTypeId);
      if (!relevantBalance) {
        throw new Error(`No balance found for leave type: ${leaveTypeId}`);
      }

      // Get all leaves for this type in current year
      const leavesResponse = await getEmployeeLeaves({
        Leave_Employee_Id: employeeId,
        Leave_LeaveType_Id: leaveTypeId
      });

      if (!leavesResponse.isSuccess) {
        throw new Error(leavesResponse.message || 'Failed to load leaves');
      }

      const allLeaves = leavesResponse.data || [];

      // Calculate total used days from all non-cancelled leaves (including pending)
      const totalUsedDays = allLeaves.reduce((total, leave) => {
        const leaveId = leave.Leave_Id || leave.leave_Id; // Handle both field name cases
        if (!leave.is_Cancelled && !leave.Is_Cancelled && parseInt(leaveId) !== parseInt(existingLeaveId)) {
          return total + (parseFloat(leave.leave_Days || leave.Leave_Days) || 0);
        }
        return total;
      }, 0);

      // Special handling for sick leave (type 2)
      const isSickLeave = leaveTypeId === 2;

      if (isSickLeave) {
        // For sick leave, validate against total combined balance (full + half + unpaid)
        const totalFullPaid = parseFloat(relevantBalance.total_FullPaidDays) || 0;
        const totalHalfPaid = parseFloat(relevantBalance.total_HalfPaidDays) || 0;
        const totalUnpaid = parseFloat(relevantBalance.total_UnpaidDays) || 0;

        const totalCombinedBalance = totalFullPaid + totalHalfPaid + totalUnpaid;
        const availableCombinedDays = totalCombinedBalance - totalUsedDays;

        console.log('Sick leave balance validation:', {
          leaveTypeId,
          totalFullPaid,
          totalHalfPaid,
          totalUnpaid,
          totalCombinedBalance,
          totalUsedDays,
          availableCombinedDays,
          requestedDays
        });

        // Round to 2 decimal places to avoid floating point issues
        const roundedRequestedDays = Math.round(requestedDays * 100) / 100;
        const roundedAvailableDays = Math.round(availableCombinedDays * 100) / 100;

        if (roundedRequestedDays > roundedAvailableDays) {
          throw new Error(`Insufficient sick leave balance. Available: ${roundedAvailableDays.toFixed(2)} days (Full: ${totalFullPaid}, Half: ${totalHalfPaid}, Unpaid: ${totalUnpaid}), Requested: ${roundedRequestedDays.toFixed(2)} days`);
        }
      } else {
        // For other leave types, use the existing logic
        const totalAvailable = parseFloat(relevantBalance.total_FullPaidDays) || 0;
        const availableDays = totalAvailable - totalUsedDays;

        console.log('Leave balance validation:', {
          leaveTypeId,
          totalAvailable,
          totalUsedDays,
          availableDays,
          requestedDays
        });

        // Round to 2 decimal places to avoid floating point issues
        const roundedRequestedDays = Math.round(requestedDays * 100) / 100;
        const roundedAvailableDays = Math.round(availableDays * 100) / 100;

        if (roundedRequestedDays > roundedAvailableDays) {
          throw new Error(`Insufficient leave balance. Available: ${roundedAvailableDays.toFixed(2)} days, Requested: ${roundedRequestedDays.toFixed(2)} days`);
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating leave balance:', error);
    throw error;
  }
};

const EmployeeLeavePage = () => {
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEmployeeLeave');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsEmployeeLeave');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsEmployeeLeave');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsEmployeeLeave');
  const isAdmin = isUserAdmin();

  // Check approve permission for Set Rejoin Date button
  const hasApprovePermission = isAdmin || (permissions.find(p => p.permission_Page_Id === 10)?.permission_Approve > 0);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [activeLeave, setActiveLeave] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [compensatoryForm, setCompensatoryForm] = useState(defaultCompensatoryForm);
  const [compensatoryLeaves, setCompensatoryLeaves] = useState([]);
  const [allLeaveTypes, setAllLeaveTypes] = useState([]);
  const [leaveCategory, setLeaveCategory] = useState("pending");
  const [selectedCompensatoryEmployee, setSelectedCompensatoryEmployee] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [payrollPeriod, setPayrollPeriod] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [employeeFilterSearchOptions, setEmployeeFilterSearchOptions] = useState([]);
  const [employeeFilterSearchLoading, setEmployeeFilterSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [filterSearchTimeout, setFilterSearchTimeout] = useState(null);
  const session = useMemo(() => getUserSession(), []);

  // Add effect to handle tab changes
  useEffect(() => {
    if (activeTab === "2") {
      // Only reset if not in edit mode
      if (!form.Leave_Id) {
        setForm(defaultForm);
        setSelectedEmployee(null);
        setSelectedLeaveType(null);
      }
      // Reload leaves list
      loadLeaves(filter);
    } else {
      // Reset credit history view when switching to add form
      setShowCreditHistory(false);
    }
  }, [activeTab]);

  // Reset page when category changes
  useEffect(() => {
    setPage(1);
  }, [leaveCategory]);

  useEffect(() => {
    if (activeTab === "3") {
      loadCompensatoryLeaves();
    }
  }, [activeTab]);

  const columns = [
    {
      header: "#",
      accessorKey: "serialNumber",
      enableColumnFilter: false,
      cell: ({ row }) => row.index + 1,
    },
    {
      header: "Employee",
      accessorKey: "employeeName",
      enableColumnFilter: false,
    },
    {
      header: "Leave Type",
      accessorKey: "leaveTypeName",
      enableColumnFilter: false,
    },
    {
      header: "From Date",
      accessorKey: "displayFromDate",
      enableColumnFilter: false,
    },
    {
      header: "To Date",
      accessorKey: "displayToDate",
      enableColumnFilter: false,
    },
    {
      header: "Rejoin Date",
      accessorKey: "displayEndDate",
      enableColumnFilter: false,
      cell: ({ row }) => {
        if (row.original.leave_RejoinDate) {
          const rejoinDate = new Date(row.original.leave_RejoinDate);
          rejoinDate.setDate(rejoinDate.getDate() + 1); // Add 1 day to show actual rejoin date
          return format(rejoinDate, 'dd/MM/yyyy');
        }
        return '-';
      },
    },
    {
      header: "Days",
      accessorKey: "leave_Days",
      enableColumnFilter: false,
    },
    {
      header: "Off Days",
      accessorKey: "offDays",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.offDays || 0,
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const leave = row.original;
        if (leave.Is_Cancelled || leave.is_Cancelled) {
          return <span className="badge bg-danger">Cancelled</span>;
        } else if (leave.leave_RejoinDate || leave.Leave_RejoinDate) {
          return <span className="badge bg-success">Completed</span>;
        } else if (leave.leave_ApprovedDate || leave.Leave_ApprovedDate) {
          const today = new Date();
          const fromDate = new Date(leave.leave_FromDate || leave.Leave_FromDate);
          if (fromDate > today) {
            return <span className="badge bg-info">Scheduled</span>;
          } else {
            return <span className="badge bg-warning">Active</span>;
          }
        } else {
          return <span className="badge bg-secondary">Pending</span>;
        }
      },
    },
    {
      header: "Actions",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const leave = row.original;
        const isEnded = leave.leave_RejoinDate || leave.Leave_RejoinDate;
        const isCancelled = leave.Is_Cancelled || leave.is_Cancelled;

        if (isEnded || isCancelled) {
          return (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewLeave(leave)}
              disabled={loading}
            >
              {loading ? <ClipLoader size={15} color="#ffffff" /> : "View"}
            </Button>
          );
        }

        return (
          <div className="d-flex gap-2">
            {hasDeletePermission && (
              <Button
                color="danger"
                size="sm"
                onClick={() => handleCancelLeave(leave)}
                disabled={loading}
              >
                {loading ? <ClipLoader size={15} color="#ffffff" /> : "Cancel Leave"}
              </Button>
            )}
            {hasEditOrHigher && (
              <Button
                color="primary"
                size="sm"
                onClick={() => handleRejoin(leave)}
                disabled={loading}
              >
                {loading ? <ClipLoader size={15} color="#ffffff" /> : "Set Rejoin Date"}
              </Button>
            )}
            {hasEditOrHigher && (
              <Button
                color="primary"
                size="sm"
                onClick={() => handleEdit(leave)}
                disabled={loading}
              >
                {loading ? <ClipLoader size={15} color="#ffffff" /> : "Edit"}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const loadPayrollPeriod = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Payroll/get`, {}, {
        headers: getAuthHeader(),
      });

      const currentPayroll = response.data.find(p => !p.payroll_IsPosted);
      if (currentPayroll) {
        const endDate = new Date(currentPayroll.payroll_Date);
        const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 1, endDate.getDate() + 1);

        // Format dates without timezone conversion
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setPayrollPeriod({
          startDate: formatDate(startDate),
          endDate: formatDate(endDate)
        });
      }
    } catch (error) {
      console.error("Error loading payroll period:", error);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadLeaves();
      loadEmployees();
      loadLeaveTypes();
      loadPayrollPeriod();

      // Set default tab for view-only users
      if (isViewOnly) {
        setActiveTab("2"); // Show Leave List tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness?.value === session.businessId && !selectedBranch) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  const loadLeaves = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      // Load employees and leave types first if not already loaded
      let currentEmployees = employees;
      let currentLeaveTypes = leaveTypes;

      if (employees.length === 0) {
        const empFilter = {};
        if (selectedBusiness) {
          empFilter.employee_Business_Id = selectedBusiness.value;
        }
        if (selectedBranch) {
          empFilter.employee_Branch_Id = selectedBranch.value;
        }
        // If user has session business/branch, use those if no selection
        if (!selectedBusiness && session.businessId) {
          empFilter.employee_Business_Id = session.businessId;
        }
        if (!selectedBranch && session.branchId && session.businessId) {
          empFilter.employee_Branch_Id = session.branchId;
        }
        const empData = await getEmployees(empFilter);
        currentEmployees = empData.map(emp => ({
          value: emp.employee_Id,
          label: emp.employee_NameEn || emp.employee_Name
        }));
        setEmployees(currentEmployees);
      }

      if (leaveTypes.length === 0) {
        const ltData = await getLeaveTypes({});
        currentLeaveTypes = ltData.map(lt => ({
          value: lt.leaveType_Id,
          label: lt.leaveType_NameEn || lt.leaveType_Name
        }));
        setLeaveTypes(currentLeaveTypes);
      }

      const response = await getEmployeeLeaves(filterData);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to load leaves');
      }

      const leavesData = response.data || [];

      // Enrich the leaves data with employee and leave type names
      const enrichedData = leavesData.map(leave => {
        const employee = currentEmployees.find(emp => emp.value === leave.leave_Employee_Id);
        const leaveType = currentLeaveTypes.find(lt => lt.value === leave.leave_LeaveType_Id);

        return {
          ...leave,
          employeeName: employee?.label || `Employee ID: ${leave.leave_Employee_Id}`,
          leaveTypeName: leaveType?.label || `Leave Type ID: ${leave.leave_LeaveType_Id}`,
          // Format dates for display
          displayFromDate: format(new Date(leave.leave_FromDate), 'dd/MM/yyyy'),
          displayToDate: format(new Date(leave.leave_ToDate), 'dd/MM/yyyy'),
          displayEndDate: leave.leave_RejoinDate ? (() => {
            const rejoinDate = new Date(leave.leave_RejoinDate);
            rejoinDate.setDate(rejoinDate.getDate() + 1); // Add 1 day to show actual rejoin date
            return format(rejoinDate, 'dd/MM/yyyy');
          })() : '-'
        };
      });

      setLeaves(enrichedData);
    } catch (error) {
      console.error('Error loading leaves:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to load leaves',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const data = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchOptions = data.map(b => ({
        value: b.branch_Id || b.Branch_Id,
        label: b.branch_NameEn || b.Branch_NameEn || b.branch_Name || b.Branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      if (selectedBusiness) {
        filter.employee_Business_Id = selectedBusiness.value;
      }
      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.value;
      }
      // If user has session business/branch, use those if no selection
      if (!selectedBusiness && session.businessId) {
        filter.employee_Business_Id = session.businessId;
      }
      if (!selectedBranch && session.branchId && session.businessId) {
        filter.employee_Branch_Id = session.branchId;
      }

      const data = await getEmployees(filter);
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name
      }));
      setEmployees(formattedEmployees);
      return formattedEmployees;
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
      return [];
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const data = await getLeaveTypes({});
      const formattedTypes = data.map(lt => ({
        value: lt.leaveType_Id,
        label: lt.leaveType_NameEn || lt.leaveType_Name
      }));
      setAllLeaveTypes(formattedTypes);
      setLeaveTypes(formattedTypes);
      return formattedTypes;
    } catch (error) {
      console.error('Error loading leave types:', error);
      Swal.fire("Error", "Failed to load leave types", "error");
      return [];
    }
  };

  // Manual search function for employee form search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  // Manual search function for employee filter search
  const searchEmployeesFilterManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeFilterSearchOptions([]);
      return;
    }

    try {
      setEmployeeFilterSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeFilterSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeFilterSearchOptions([]);
    } finally {
      setEmployeeFilterSearchLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if ((name === "Leave_FromDate" || name === "Leave_ToDate") && form.Leave_LeaveType_Id === 9) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        Swal.fire({
          title: "Invalid Date",
          text: "For unpaid leave, you can only select today or future dates",
          icon: "warning",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000
        });
        return;
      }
    }
    setForm(prev => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : (value || '') };
      if ((name === "Leave_FromDate" || name === "Leave_ToDate" || name === "OffDays" || name === "Is_HalfDay") && updated.Leave_FromDate && updated.Leave_ToDate) {
        const { requestedDays, leaveDays } = calculateLeaveDaysUtil(
          updated.Leave_FromDate,
          updated.Leave_ToDate,
          updated.OffDays,
          updated.Is_HalfDay
        );
        updated.Leave_RequestedDays = requestedDays;
        updated.Leave_Days = leaveDays;
      }
      return updated;
    });
  };

  // Reload employees when business/branch changes
  useEffect(() => {
    if (businesses.length > 0) {
      loadEmployees();
    }
  }, [selectedBusiness, selectedBranch]);

  const handleBusinessChange = (selectedOption) => {
    setSelectedBusiness(selectedOption);
    setSelectedBranch(null);
    setFilter(prev => ({ ...prev, Leave_Employee_Id: null }));
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
    setFilter(prev => ({ ...prev, Leave_Employee_Id: null }));
  };

  const handleSelectChange = async (selectedOption, field) => {
    if (field === "Leave_LeaveType_Id") {
      // Check if compensatory leave is selected
      if (selectedOption?.value === 8) { // Compensatory Leave Type ID
        // Find compensatory leave balance
        const compensatoryBalance = leaveBalances.find(b => b.leaveType_Id === 8);

        if (!compensatoryBalance || compensatoryBalance.available_FullPaidDays <= 0) {
          Swal.fire({
            title: "No Compensatory Leave Available",
            text: "You don't have any compensatory leave days available. Please select another leave type.",
            icon: "warning"
          });
          return; // Don't update the selected leave type
        }
      }

      // If switching to unpaid leave, clear any past dates
      if (selectedOption?.value === 9) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        setForm(prev => {
          const updated = {
            ...prev,
            Leave_LeaveType_Id: selectedOption.value
          };

          // Check and clear from date if it's in the past
          if (prev.Leave_FromDate) {
            const fromDate = new Date(prev.Leave_FromDate);
            if (fromDate < today) {
              updated.Leave_FromDate = '';
              updated.Leave_ToDate = '';
              updated.Leave_RequestedDays = 0;
              updated.Leave_Days = 0;
            }
          }

          // Check and clear to date if it's in the past
          if (prev.Leave_ToDate) {
            const toDate = new Date(prev.Leave_ToDate);
            if (toDate < today) {
              updated.Leave_ToDate = '';
              updated.Leave_RequestedDays = 0;
              updated.Leave_Days = 0;
            }
          }

          return updated;
        });

        // Show info message about date restrictions
        Swal.fire({
          title: "Unpaid Leave Selected",
          text: "For unpaid leave, you can only select today or future dates",
          icon: "info",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000
        });
      }

      setSelectedLeaveType(selectedOption);
      setForm(prev => ({
        ...prev,
        [field]: selectedOption ? selectedOption.value : null
      }));
    } else if (field === "Leave_Employee_Id") {
      setForm(prev => ({
        ...prev,
        [field]: selectedOption ? selectedOption.value : null
      }));

      setSelectedEmployee(selectedOption);

      if (selectedOption) {
        setLeaveBalances([]);
        setIsLoadingBalance(true);
        setIsLoadingCredits(true);

        try {
          // Get employee details for gender
          const employeeData = await getEmployees({ employee_Id: selectedOption.value });
          if (!employeeData || employeeData.length === 0) {
            throw new Error('Employee not found');
          }
          const employee = employeeData[0];
          setSelectedEmployeeData(employee);
          const employeeGenderId = employee.employee_Gender_Id;

          // Filter leave types based on gender
          const filteredLeaveTypes = allLeaveTypes.filter(lt => {
            // Maternity Leave (ID: 3) - Only for women (Gender_Id: 5)
            if (lt.value === 3) return employeeGenderId === 5;
            // Paternity Leave (ID: 4) - Only for men (Gender_Id: 1)
            if (lt.value === 4) return employeeGenderId === 1;
            // All other leaves are available for everyone
            return true;
          });

          // Update the leave types state with filtered options
          setLeaveTypes(filteredLeaveTypes);

          // Get existing balances
          const balanceHistory = await getLeaveBalanceHistory({
            Employee_Id: selectedOption.value,
            BalanceYear: new Date().getFullYear()
          });

          if (!balanceHistory || balanceHistory.length === 0) {
            await Swal.fire({
              title: "No Leave Balance Found",
              text: "Please update leave balances before applying for leave.",
              icon: "warning",
              confirmButtonText: "OK"
            });
            setLeaveBalances([]);
            return;
          }

          await loadAvailableYears(selectedOption.value);
          await loadLeaveBalances(selectedOption.value);
          await checkActiveLeave(selectedOption.value);
        } catch (error) {
          console.error('Error in processing:', error);
          Swal.fire('Error', 'Failed to load leave balances: ' + error.message, 'error');
        } finally {
          setIsLoadingBalance(false);
          setIsLoadingCredits(false);
        }
      } else {
        setLeaveBalances([]);
        // Reset leave types to all options when no employee is selected
        setLeaveTypes(allLeaveTypes);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!form.Leave_Id) {
        if (!form.Leave_Employee_Id) throw new Error('Please select an employee');
        if (!form.Leave_LeaveType_Id) throw new Error('Please select a leave type');
        if (!form.Leave_FromDate) throw new Error('Please select a from date');
        if (!form.Leave_ToDate) throw new Error('Please select a to date');
        if (!form.Leave_Comments || form.Leave_Comments.trim() === '') throw new Error('Please provide comments/reason for the leave');
      }
      const { requestedDays, leaveDays } = calculateLeaveDaysUtil(
        form.Leave_FromDate,
        form.Leave_ToDate,
        form.OffDays,
        form.Is_HalfDay
      );
      if (leaveDays <= 0) {
        Swal.fire({
          title: 'Invalid Leave',
          text: 'Leave days must be greater than zero. Please check your dates and off days.',
          icon: 'warning',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000
        });
        return;
      }
      setLoading(true);
      await validateLeaveBalance(
        form.Leave_Employee_Id,
        form.Leave_LeaveType_Id,
        leaveDays,
        form.Leave_FromDate,
        form.Leave_ToDate,
        form.Leave_Id
      );

      // Check for booking conflicts before saving
      console.log("🚀 Starting conflict check process...");
      try {
        console.log("🔍 Checking booking conflicts for:", {
          employeeId: form.Leave_Employee_Id,
          fromDate: form.Leave_FromDate,
          toDate: form.Leave_ToDate
        });

        console.log("📞 About to call checkBookingConflicts API...");
        console.log("🔧 checkBookingConflicts function:", typeof checkBookingConflicts);
        const conflictResponse = await checkBookingConflicts(
          form.Leave_Employee_Id,
          form.Leave_FromDate,
          form.Leave_ToDate
        );
        console.log("📞 API call completed");

        console.log("📋 Conflict check response:", conflictResponse);

        if (conflictResponse.data && conflictResponse.data.length > 0) {
          console.log("⚠️ Conflicts found:", conflictResponse.data);

          const conflictList = conflictResponse.data.map(conflict =>
            `• Booking #${conflict.booking_Id} - ${conflict.customer_NameEn} - ${new Date(conflict.booking_Date).toLocaleDateString('en-GB')}`
          ).join('\n');

          const result = await Swal.fire({
            title: 'Booking Conflict Detected',
            html: `
              <p>This employee has existing bookings during the requested leave period:</p>
              <div style="text-align: left; margin: 10px 0;">
                <pre style="white-space: pre-wrap; font-family: inherit;">${conflictList}</pre>
              </div>
              <p><strong>Do you want to proceed with the leave request?</strong></p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Proceed',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
          });

          if (!result.isConfirmed) {
            console.log("❌ User cancelled due to conflicts");
            return; // User cancelled, don't proceed with save
          }
        } else {
          console.log("✅ No conflicts found");
        }
      } catch (conflictError) {
        console.error("❌ Error checking booking conflicts:", conflictError);
        // Continue with save even if conflict check fails
      }

      const leaveData = {
        ...form,
        Leave_RequestedDays: requestedDays,
        Leave_Days: leaveDays,
        Leave_RejoinDate: form.Leave_RejoinDate || null
      };
      await saveEmployeeLeave(leaveData);
      Swal.fire({
        title: "Success",
        text: form.Leave_Id ? "Leave updated successfully" : "Leave added successfully",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
      setForm(defaultForm);
      setSelectedEmployee(null);
      setSelectedLeaveType(null);
      setActiveTab("2");
      loadLeaves(filter);
    } catch (error) {
      console.error("Error saving leave:", error);

      // Get the error message
      const errorMessage = error.message || "Failed to save leave";

      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    } finally {
      setLoading(false);
    }
  };

  const saveLeave = async () => {
    console.log("🚀 SAVE LEAVE FUNCTION CALLED!");
    try {
      const { requestedDays, leaveDays } = calculateLeaveDaysUtil(
        form.Leave_FromDate,
        form.Leave_ToDate,
        form.OffDays,
        form.Is_HalfDay
      );
      console.log("🔍 About to call validateLeaveBalance...");
      try {
        await validateLeaveBalance(
          form.Leave_Employee_Id,
          form.Leave_LeaveType_Id,
          leaveDays,
          form.Leave_FromDate,
          form.Leave_ToDate,
          form.Leave_Id
        );
        console.log("✅ Leave balance validation passed, proceeding to conflict check...");
      } catch (validationError) {
        console.error("❌ Leave balance validation failed:", validationError);
        throw validationError; // Re-throw to maintain existing behavior
      }

      // Check for booking conflicts before saving
      console.log("🚀 Starting conflict check process...");
      try {
        console.log("🔍 Checking booking conflicts for:", {
          employeeId: form.Leave_Employee_Id,
          fromDate: form.Leave_FromDate,
          toDate: form.Leave_ToDate
        });

        console.log("📞 About to call checkBookingConflicts API...");
        console.log("🔧 checkBookingConflicts function:", typeof checkBookingConflicts);
        const conflictResponse = await checkBookingConflicts(
          form.Leave_Employee_Id,
          form.Leave_FromDate,
          form.Leave_ToDate
        );
        console.log("📞 API call completed");

        console.log("📋 Conflict check response:", conflictResponse);

        if (conflictResponse.data && conflictResponse.data.length > 0) {
          console.log("⚠️ Conflicts found:", conflictResponse.data);

          const conflictList = conflictResponse.data.map(conflict =>
            `• Booking #${conflict.booking_Id} - ${conflict.customer_NameEn} - ${new Date(conflict.booking_Date).toLocaleDateString('en-GB')}`
          ).join('\n');

          const result = await Swal.fire({
            title: 'Booking Conflict Detected',
            html: `
              <p>This employee has existing bookings during the requested leave period:</p>
              <div style="text-align: left; margin: 10px 0;">
                <pre style="white-space: pre-wrap; font-family: inherit;">${conflictList}</pre>
              </div>
              <p><strong>Do you want to proceed with the leave request?</strong></p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Proceed',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
          });

          if (!result.isConfirmed) {
            console.log("❌ User cancelled due to conflicts");
            return; // User cancelled, don't proceed with save
          }
        } else {
          console.log("✅ No conflicts found");
        }
      } catch (conflictError) {
        console.error("❌ Error checking booking conflicts:", conflictError);
        // Continue with save even if conflict check fails
      }

      const leaveData = {
        ...form,
        Leave_RequestedDays: requestedDays,
        Leave_Days: leaveDays,
        Leave_RejoinDate: form.Leave_RejoinDate || null
      };
      await saveEmployeeLeave(leaveData);
      Swal.fire({
        title: "Success",
        text: form.Leave_Id ? "Leave updated successfully" : "Leave added successfully",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
      });
      setForm(defaultForm);
      setSelectedEmployee(null);
      setSelectedLeaveType(null);
      setActiveTab("2");
      loadLeaves(filter);
    } catch (error) {
      console.error("Error saving leave:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to save leave: " + (error.response?.data?.title || error.message),
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
      });
    }
  };

  const handleEdit = async (leave) => {
    try {
      // Switch to the add/edit tab
      setActiveTab("1");

      // Format dates properly
      const formData = {
        Leave_Id: leave.leave_Id || 0,
        Leave_Employee_Id: leave.leave_Employee_Id,
        Leave_LeaveType_Id: leave.leave_LeaveType_Id,
        Leave_RequestDate: leave.leave_RequestDate || new Date().toISOString(),
        Leave_FromDate: leave.leave_FromDate ? format(new Date(leave.leave_FromDate), 'yyyy-MM-dd') : '',
        Leave_ToDate: leave.leave_ToDate ? format(new Date(leave.leave_ToDate), 'yyyy-MM-dd') : '',
        Leave_RequestedDays: leave.leave_RequestedDays || 0,
        Leave_Days: leave.leave_Days || 0,
        Leave_Comments: leave.leave_Comments || '',
        LeaveType_CreatedDate: leave.leaveType_CreatedDate || new Date().toISOString(),
        LeaveType_CreatedBy: leave.leaveType_CreatedBy || Number(localStorage.getItem("userId")) || 1,
        Leave_ApprovedDate: leave.leave_ApprovedDate || new Date().toISOString(),
        Leave_ApprovedBy: leave.leave_ApprovedBy || Number(localStorage.getItem("userId")) || 1,
        OffDays: leave.offDays || 0,
        Is_HalfDay: leave.is_HalfDay || false,
        Leave_RejoinDate: null // Ensure rejoin date is null when editing
      };

      // Set form data
      setForm(formData);

      // Set selected employee and leave type for dropdowns
      const employeeOption = employees.find(emp => emp.value === leave.leave_Employee_Id);
      const leaveTypeOption = leaveTypes.find(lt => lt.value === leave.leave_LeaveType_Id);

      setSelectedEmployee(employeeOption || null);
      setSelectedLeaveType(leaveTypeOption || null);

      // Load employee balances, credit history and check active leave
      if (employeeOption) {
        setIsLoadingBalance(true);
        setIsLoadingCredits(true);

        await Promise.all([
          loadAvailableYears(employeeOption.value),
          loadLeaveBalances(employeeOption.value),
          getEmployeeCreditHistory(employeeOption.value).then(creditHistoryData => {
            setCreditHistory(creditHistoryData);
          }),
          checkActiveLeave(employeeOption.value),
          // Load employee data for display
          getEmployees({ employee_Id: employeeOption.value }).then(employeeData => {
            if (employeeData && employeeData.length > 0) {
              setSelectedEmployeeData(employeeData[0]);
            }
          })
        ]);
      }
    } catch (error) {
      console.error("Error in handleEdit:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load leave details: " + error.message,
        icon: "error"
      });
    } finally {
      setIsLoadingBalance(false);
      setIsLoadingCredits(false);
    }
  };

  const handleViewLeave = (leave) => {
    setSelectedLeave(leave);
    setViewModalOpen(true);
  };

  const handleRejoin = async (leave) => {
    try {
      const leavesResponse = await getEmployeeLeaves({ Leave_Employee_Id: leave.leave_Employee_Id });
      if (!leavesResponse.isSuccess) {
        throw new Error(leavesResponse.message || 'Failed to load leaves');
      }
      const allLeaves = leavesResponse.data || [];
      const activeLeaves = allLeaves.filter(l =>
        !l.is_Cancelled &&
        !l.Is_Cancelled &&
        l.leave_Id !== leave.leave_Id
      );

      // Get current date for validation
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of day to allow selecting today

      // Calculate actual days for display in modal
      const fromDate = new Date(leave.leave_FromDate);
      const todayDate = new Date(today.toISOString().split("T")[0]);
      fromDate.setHours(0, 0, 0, 0);
      todayDate.setHours(0, 0, 0, 0);

      // Calculate effective end date (today - 1 day)
      const effectiveEndDateForDisplay = new Date(todayDate);
      effectiveEndDateForDisplay.setDate(effectiveEndDateForDisplay.getDate() - 1);

      const totalDaysForDisplay = Math.ceil((effectiveEndDateForDisplay - fromDate) / (1000 * 60 * 60 * 24)) + 1;
      let actualDaysForDisplay = Math.max(0, totalDaysForDisplay - (Number(leave.offDays) || 0));
      if (leave.is_HalfDay || leave.Is_HalfDay) {
        if (totalDaysForDisplay === 1) {
          actualDaysForDisplay = 0.5;
        } else {
          actualDaysForDisplay = actualDaysForDisplay - 0.5;
        }
      }
      actualDaysForDisplay = Math.round(actualDaysForDisplay * 100) / 100;

      // Calculate date range for rejoin date selection
      const isUnpaidLeave = leave.leave_LeaveType_Id === 9;
      const minDate = isUnpaidLeave ?
        today.toISOString().split("T")[0] : // For unpaid leave, only current date
        (() => {
          const fromDate = new Date(leave.leave_FromDate);
          fromDate.setDate(fromDate.getDate() + 1); // From date + 1 day
          return fromDate.toISOString().split("T")[0];
        })();
      const maxDate = today.toISOString().split("T")[0]; // Current date

      const { value: rejoinDate } = await Swal.fire({
        title: "Set Rejoin Date",
        html: `
          <div class="text-start">
            <p><strong>Leave Details:</strong></p>
            <p>From: ${format(new Date(leave.leave_FromDate), 'dd/MM/yyyy')}</p>
            <p>To: ${format(new Date(leave.leave_ToDate), 'dd/MM/yyyy')}</p>
            <p>Original requested days: ${calculateLeaveDaysUtil(leave.leave_FromDate, leave.leave_ToDate, leave.offDays, leave.is_HalfDay || leave.Is_HalfDay).requestedDays}</p>
            <p>Off days: ${leave.offDays || 0}</p>
            <p><strong>Actual leave days taken: <span id="actualDaysDisplay">${actualDaysForDisplay}</span></strong></p>
          </div>
          <p class="mt-3">Select Rejoin Date:</p>
        `,
        input: "date",
        inputValue: today.toISOString().split("T")[0],
        inputAttributes: {
          min: minDate,
          max: maxDate
        },
        showCancelButton: true,
        confirmButtonText: "Update",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return 'Please select a rejoin date';
          }
          return null;
        },
        didOpen: () => {
          const dateInput = Swal.getInput();
          const actualDaysElement = document.getElementById('actualDaysDisplay');

          if (dateInput && actualDaysElement) {
            const updateCalculation = () => {
              const val = dateInput.value;
              if (!val) return;

              const selectedDate = new Date(val);
              // Calculate effective end date (selected date - 1 day)
              const effectiveEndDate = new Date(selectedDate);
              effectiveEndDate.setDate(effectiveEndDate.getDate() - 1);

              const fromDate = leave.leave_FromDate || leave.Leave_FromDate;
              const offDays = leave.offDays || leave.OffDays || 0;
              const isHalfDay = leave.is_HalfDay || leave.Is_HalfDay || false;

              // Use existing calculation function
              const { leaveDays } = calculateLeaveDaysUtil(
                fromDate,
                effectiveEndDate.toISOString().split('T')[0],
                offDays,
                isHalfDay
              );

              console.log('Rejoin calculation update:', { 
                selectedDate: val, 
                fromDate, 
                effectiveEndDate: effectiveEndDate.toISOString().split('T')[0], 
                leaveDays 
              });

              actualDaysElement.textContent = leaveDays;
            };

            dateInput.addEventListener('input', updateCalculation);
            dateInput.addEventListener('change', updateCalculation);
          }
        }
      });
      if (rejoinDate) {
        const fromDate = new Date(leave.leave_FromDate);
        const rejoinDateObj = new Date(rejoinDate);
        fromDate.setHours(0, 0, 0, 0);
        rejoinDateObj.setHours(0, 0, 0, 0);



        // Calculate effective end date (rejoin date - 1 day)
        const effectiveEndDate = new Date(rejoinDateObj);
        effectiveEndDate.setDate(effectiveEndDate.getDate() - 1);

        const totalDays = Math.ceil((effectiveEndDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
        let actualDays = Math.max(0, totalDays - (Number(leave.offDays) || 0));
        if (leave.is_HalfDay || leave.Is_HalfDay) {
          if (totalDays === 1) {
            actualDays = 0.5;
          } else {
            actualDays = actualDays - 0.5;
          }
        }
        actualDays = Math.round(actualDays * 100) / 100;
        const originalDays = Math.round(leave.leave_Days * 100) / 100;

        // Check for overlapping leaves
        const overlappingLeave = activeLeaves.find(l => {
          const otherFromDate = new Date(l.leave_FromDate);
          const otherToDate = new Date(l.leave_ToDate);
          otherFromDate.setHours(0, 0, 0, 0);
          otherToDate.setHours(0, 0, 0, 0);

          return (fromDate <= otherToDate && effectiveEndDate >= otherFromDate) ||
            fromDate.getTime() === otherFromDate.getTime() ||
            effectiveEndDate.getTime() === otherToDate.getTime();
        });

        if (overlappingLeave) {
          Swal.fire({
            title: "Leave Overlap",
            text: `This leave period overlaps with another leave from ${format(new Date(overlappingLeave.leave_FromDate), 'dd/MM/yyyy')} to ${format(new Date(overlappingLeave.leave_ToDate), 'dd/MM/yyyy')}`,
            icon: "error"
          });
          return;
        }

        // For Annual Leave (type 1): validate balance at rejoin time.
        // The employee may have applied when balance was insufficient, but by the time
        // they return, monthly credits should have accumulated enough days.
        if (leave.leave_LeaveType_Id === 1) {
          try {
            const currentYear = new Date().getFullYear();
            const annualBalanceHistory = await getLeaveBalanceHistory({
              Employee_Id: leave.leave_Employee_Id,
              BalanceYear: currentYear
            });
            const annualBalance = annualBalanceHistory?.find(b => b.leaveType_Id === 1);

            if (!annualBalance) {
              Swal.fire({
                title: 'No Annual Leave Balance Found',
                text: 'No Annual Leave balance record exists for this employee. Please process leave credits first.',
                icon: 'error'
              });
              return;
            }

            // Get all non-cancelled Annual Leave records to calculate total used (excluding this leave)
            const allLeavesResp = await getEmployeeLeaves({
              Leave_Employee_Id: leave.leave_Employee_Id,
              Leave_LeaveType_Id: 1
            });
            const allAnnualLeaves = allLeavesResp?.data || [];
            const usedDays = allAnnualLeaves.reduce((total, l) => {
              if (!l.is_Cancelled && !l.Is_Cancelled && l.leave_Id !== leave.leave_Id) {
                return total + (parseFloat(l.leave_Days) || 0);
              }
              return total;
            }, 0);

            const totalAllocated = parseFloat(annualBalance.total_FullPaidDays) || 0;
            const availableDays = Math.round((totalAllocated - usedDays) * 100) / 100;
            const roundedActualDays = Math.round(actualDays * 100) / 100;

            if (roundedActualDays > availableDays) {
              Swal.fire({
                title: 'Insufficient Annual Leave Balance',
                text: `This employee does not have enough Annual Leave balance to rejoin. Available: ${availableDays.toFixed(2)} days, Required: ${roundedActualDays.toFixed(2)} days. Please wait until sufficient credits are accumulated.`,
                icon: 'warning'
              });
              return;
            }
          } catch (error) {
            Swal.fire('Error', 'Failed to verify Annual Leave balance: ' + error.message, 'error');
            return;
          }
        } else {
          // For all other leave types, run the existing balance validation at rejoin
          try {
            await validateLeaveBalance(
              leave.leave_Employee_Id,
              leave.leave_LeaveType_Id,
              actualDays,
              leave.leave_FromDate,
              leave.leave_ToDate,
              leave.leave_Id
            );
          } catch (error) {
            Swal.fire('Error', error.message, 'error');
            return;
          }
        }
        // Always use the calculated actual days
        leave.leave_Days = actualDays;
        // Save effective end date (rejoin date - 1 day) to database
        const effectiveEndDateForDB = new Date(rejoinDate);
        effectiveEndDateForDB.setDate(effectiveEndDateForDB.getDate() - 1);

        const updatedLeave = {
          ...leave,
          leave_RejoinDate: effectiveEndDateForDB.toISOString().split('T')[0],
          leave_Days: leave.leave_Days
        };
        await saveEmployeeLeave(updatedLeave);
        await updateLeaveBalance(leave);
        await loadLeaves(filter);
        if (selectedEmployee) {
          await loadLeaveBalances(selectedEmployee.value);
        }
        Swal.fire({
          title: "Success",
          text: "Rejoin date updated successfully",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000
        });
      }
    } catch (error) {
      console.error("Error updating end date:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update rejoin date: " + error.message,
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
      });
    }
  };

  const updateLeaveBalance = async (leave) => {
    try {
      const currentYear = new Date().getFullYear();
      const fromYear = new Date(leave.leave_FromDate || leave.Leave_FromDate).getFullYear();
      const toYear = new Date(leave.leave_ToDate || leave.Leave_ToDate).getFullYear();
      const isCrossYearLeave = fromYear !== toYear;

      // For Annual Leave (ID: 1) and Compensatory Leave (ID: 8), always use current year balance
      const isCarryForwardLeave = (leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id) === 1 ||
        (leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id) === 8;

      if (isCrossYearLeave && !isCarryForwardLeave) {
        // Handle cross-year leave balance update
        const { fromYear: daysInFromYear, toYear: daysInToYear } = calculateDaysPerYear(
          leave.leave_FromDate || leave.Leave_FromDate,
          leave.leave_ToDate || leave.Leave_ToDate,
          leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id
        );

        // Get balances for both years
        const [balanceHistoryFromYear, balanceHistoryToYear] = await Promise.all([
          getLeaveBalanceHistory({
            Employee_Id: leave.leave_Employee_Id,
            BalanceYear: fromYear
          }),
          getLeaveBalanceHistory({
            Employee_Id: leave.leave_Employee_Id,
            BalanceYear: toYear
          })
        ]);

        const relevantBalanceFromYear = balanceHistoryFromYear.find(b =>
          b.leaveType_Id === (leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id)
        );
        const relevantBalanceToYear = balanceHistoryToYear.find(b =>
          b.leaveType_Id === (leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id)
        );

        if (!relevantBalanceFromYear || !relevantBalanceToYear) {
          throw new Error(`No balance found for leave type: ${leave.leave_LeaveType_Id} in one or both years`);
        }

        // Get all leaves for both years
        const leavesResponse = await getEmployeeLeaves({
          Leave_Employee_Id: leave.leave_Employee_Id,
          Leave_LeaveType_Id: leave.leave_LeaveType_Id
        });

        if (!leavesResponse.isSuccess) {
          throw new Error(leavesResponse.message || 'Failed to load leaves');
        }

        const allLeaves = leavesResponse.data || [];

        // Calculate used days for each year using cross-year logic
        // Only count leaves that have a rejoin date (completed leaves)
        const usedDaysFromYear = allLeaves.reduce((total, currentLeave) => {
          // Only count non-cancelled leaves that have a rejoin date (completed)
          const hasRejoinDate = currentLeave.leave_RejoinDate || currentLeave.Leave_RejoinDate;
          if (!currentLeave.is_Cancelled && !currentLeave.Is_Cancelled && hasRejoinDate) {
            const leaveFromYear = new Date(currentLeave.leave_FromDate).getFullYear();
            const leaveToYear = new Date(currentLeave.leave_ToDate).getFullYear();
            const isCrossYearLeave = leaveFromYear !== leaveToYear;

            if (isCrossYearLeave) {
              // For cross-year leaves, calculate days for this specific year
              const { fromYear: daysInFromYear, toYear: daysInToYear } = calculateDaysPerYear(
                currentLeave.leave_FromDate,
                currentLeave.leave_ToDate,
                currentLeave.leave_LeaveType_Id
              );

              if (fromYear === leaveFromYear) {
                return total + daysInFromYear;
              } else if (fromYear === leaveToYear) {
                return total + daysInToYear;
              }
              return total;
            } else {
              // Single year leave
              if (leaveFromYear === fromYear) {
                return total + (currentLeave.leave_Days || 0);
              }
            }
          }
          return total;
        }, 0);

        const usedDaysToYear = allLeaves.reduce((total, currentLeave) => {
          // Only count non-cancelled leaves that have a rejoin date (completed)
          const hasRejoinDate = currentLeave.leave_RejoinDate || currentLeave.Leave_RejoinDate;
          if (!currentLeave.is_Cancelled && !currentLeave.Is_Cancelled && hasRejoinDate) {
            const leaveFromYear = new Date(currentLeave.leave_FromDate).getFullYear();
            const leaveToYear = new Date(currentLeave.leave_ToDate).getFullYear();
            const isCrossYearLeave = leaveFromYear !== leaveToYear;

            if (isCrossYearLeave) {
              // For cross-year leaves, calculate days for this specific year
              const { fromYear: daysInFromYear, toYear: daysInToYear } = calculateDaysPerYear(
                currentLeave.leave_FromDate,
                currentLeave.leave_ToDate,
                currentLeave.leave_LeaveType_Id
              );

              if (toYear === leaveFromYear) {
                return total + daysInFromYear;
              } else if (toYear === leaveToYear) {
                return total + daysInToYear;
              }
              return total;
            } else {
              // Single year leave
              if (leaveToYear === toYear) {
                return total + (currentLeave.leave_Days || 0);
              }
            }
          }
          return total;
        }, 0);

        console.log('Cross-year leave balance update:', {
          fromYear, toYear, daysInFromYear, daysInToYear,
          usedDaysFromYear, usedDaysToYear
        });

        // Special handling for sick leave (type 2)
        const isSickLeave = relevantBalanceFromYear.leaveType_Id === 2;

        if (isSickLeave) {
          // Update from year balance
          let remainingDaysFrom = usedDaysFromYear;
          const usedFullPaidFrom = Math.min(remainingDaysFrom, relevantBalanceFromYear.total_FullPaidDays || 0);
          remainingDaysFrom -= usedFullPaidFrom;
          const usedHalfPaidFrom = Math.min(remainingDaysFrom, relevantBalanceFromYear.total_HalfPaidDays || 0);
          remainingDaysFrom -= usedHalfPaidFrom;
          const usedUnpaidFrom = Math.min(remainingDaysFrom, relevantBalanceFromYear.total_UnpaidDays || 0);

          // Update to year balance
          let remainingDaysTo = usedDaysToYear;
          const usedFullPaidTo = Math.min(remainingDaysTo, relevantBalanceToYear.total_FullPaidDays || 0);
          remainingDaysTo -= usedFullPaidTo;
          const usedHalfPaidTo = Math.min(remainingDaysTo, relevantBalanceToYear.total_HalfPaidDays || 0);
          remainingDaysTo -= usedHalfPaidTo;
          const usedUnpaidTo = Math.min(remainingDaysTo, relevantBalanceToYear.total_UnpaidDays || 0);

          const updatedBalanceFromYear = {
            ...relevantBalanceFromYear,
            history_Id: relevantBalanceFromYear.history_Id,
            used_FullPaidDays: usedFullPaidFrom,
            used_HalfPaidDays: usedHalfPaidFrom,
            used_UnpaidDays: usedUnpaidFrom,
            lastResetDate: new Date().toISOString()
          };

          const updatedBalanceToYear = {
            ...relevantBalanceToYear,
            history_Id: relevantBalanceToYear.history_Id,
            used_FullPaidDays: usedFullPaidTo,
            used_HalfPaidDays: usedHalfPaidTo,
            used_UnpaidDays: usedUnpaidTo,
            lastResetDate: new Date().toISOString()
          };

          console.log('Updating cross-year balances:', { updatedBalanceFromYear, updatedBalanceToYear });
          await Promise.all([
            saveLeaveBalanceHistory(updatedBalanceFromYear),
            saveLeaveBalanceHistory(updatedBalanceToYear)
          ]);
        } else {
          // For non-sick leave types
          const updatedBalanceFromYear = {
            ...relevantBalanceFromYear,
            history_Id: relevantBalanceFromYear.history_Id,
            used_FullPaidDays: usedDaysFromYear,
            lastResetDate: new Date().toISOString()
          };

          const updatedBalanceToYear = {
            ...relevantBalanceToYear,
            history_Id: relevantBalanceToYear.history_Id,
            used_FullPaidDays: usedDaysToYear,
            lastResetDate: new Date().toISOString()
          };

          console.log('Updating cross-year balances:', { updatedBalanceFromYear, updatedBalanceToYear });
          await Promise.all([
            saveLeaveBalanceHistory(updatedBalanceFromYear),
            saveLeaveBalanceHistory(updatedBalanceToYear)
          ]);
        }
      } else {
        // Original single-year logic (unchanged)
        const balanceYear = isCarryForwardLeave ? currentYear : fromYear;

        const balanceHistory = await getLeaveBalanceHistory({
          Employee_Id: leave.leave_Employee_Id,
          BalanceYear: balanceYear
        });

        console.log('Current balance history:', balanceHistory);

        // Find the relevant balance record
        const relevantBalance = balanceHistory.find(b =>
          b.leaveType_Id === (leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id)
        );

        console.log('Found relevant balance:', relevantBalance);

        if (!relevantBalance) {
          throw new Error(`No balance found for leave type: ${leave.leave_LeaveType_Id}`);
        }

        // Get all leaves for this type in current year
        const leavesResponse = await getEmployeeLeaves({
          Leave_Employee_Id: leave.leave_Employee_Id,
          Leave_LeaveType_Id: leave.leave_LeaveType_Id
        });

        if (!leavesResponse.isSuccess) {
          throw new Error(leavesResponse.message || 'Failed to load leaves');
        }

        const allLeaves = leavesResponse.data || [];

        // Calculate total used days from all completed leaves only
        // Only count leaves that have a rejoin date (completed leaves)
        const totalUsedDays = allLeaves.reduce((total, currentLeave) => {
          // Only count non-cancelled leaves that have a rejoin date (completed)
          const hasRejoinDate = currentLeave.leave_RejoinDate || currentLeave.Leave_RejoinDate;
          if (!currentLeave.is_Cancelled && !currentLeave.Is_Cancelled && hasRejoinDate) {
            return total + (currentLeave.leave_Days || 0);
          }
          return total;
        }, 0);

        console.log('Total used days from all leaves:', totalUsedDays);

        // Special handling for sick leave (type 2)
        const isSickLeave = relevantBalance.leaveType_Id === 2;
        let updatedBalance;

        if (isSickLeave) {
          // Get the limits for each category
          const fullPaidLimit = relevantBalance.total_FullPaidDays || 0;
          const halfPaidLimit = relevantBalance.total_HalfPaidDays || 0;
          const unpaidLimit = relevantBalance.total_UnpaidDays || 0;

          // Calculate used days in each category
          let remainingDays = totalUsedDays;
          const usedFullPaid = Math.min(remainingDays, fullPaidLimit);
          remainingDays -= usedFullPaid;

          const usedHalfPaid = Math.min(remainingDays, halfPaidLimit);
          remainingDays -= usedHalfPaid;

          const usedUnpaid = Math.min(remainingDays, unpaidLimit);

          updatedBalance = {
            ...relevantBalance,
            history_Id: relevantBalance.history_Id,
            used_FullPaidDays: usedFullPaid,
            used_HalfPaidDays: usedHalfPaid,
            used_UnpaidDays: usedUnpaid,
            lastResetDate: new Date().toISOString()
          };
        } else {
          // For non-sick leave types, maintain the existing behavior
          updatedBalance = {
            ...relevantBalance,
            history_Id: relevantBalance.history_Id,
            used_FullPaidDays: totalUsedDays,
            lastResetDate: new Date().toISOString()
          };
        }

        console.log('Updating balance with:', updatedBalance);
        await saveLeaveBalanceHistory(updatedBalance);
      }

      // Refresh balances after update
      await loadLeaveBalances(leave.leave_Employee_Id, selectedYear);
    } catch (error) {
      console.error('Error updating balance:', error);
      throw new Error('Failed to update leave balance: ' + error.message);
    }
  };

  const loadLeaveBalances = async (employeeId, year = selectedYear) => {
    setIsLoadingBalance(true);
    try {
      console.log('Loading balances for employee:', employeeId, 'year:', year);

      // Get balances for selected year
      const balanceData = await getLeaveBalanceHistory({
        employee_Id: employeeId,
        BalanceYear: year
      });

      if (!balanceData || balanceData.length === 0) {
        throw new Error('No leave balance found. Please process leave credits first.');
      }

      console.log('Balance data from DB:', balanceData);

      // For Annual Leave (ID: 1) and Compensatory Leave (ID: 8), always use current year balance
      const currentYear = new Date().getFullYear();
      let annualAndCompensatoryBalances = [];

      if (year !== currentYear) {
        // If viewing a different year, get Annual and Compensatory from current year
        const currentYearBalances = await getLeaveBalanceHistory({
          employee_Id: employeeId,
          BalanceYear: currentYear
        });
        annualAndCompensatoryBalances = currentYearBalances ?
          currentYearBalances.filter(b => b.leaveType_Id === 1 || b.leaveType_Id === 8) : [];
      } else {
        // If viewing current year, get Annual and Compensatory from selected year data
        annualAndCompensatoryBalances = balanceData.filter(b => b.leaveType_Id === 1 || b.leaveType_Id === 8);
      }

      // Get other leaves from selected year
      const otherBalances = balanceData.filter(b => b.leaveType_Id !== 1 && b.leaveType_Id !== 8);

      // Combine balances
      const combinedBalances = [...annualAndCompensatoryBalances, ...otherBalances];

      console.log('Year being viewed:', year);
      console.log('Annual and Compensatory balances:', annualAndCompensatoryBalances);
      console.log('Other balances:', otherBalances);
      console.log('Combined balances:', combinedBalances);

      const leaveResponse = await getEmployeeLeaves({ Leave_Employee_Id: employeeId });
      if (!leaveResponse.isSuccess) {
        throw new Error(leaveResponse.message || 'Failed to load leaves');
      }
      const leaveData = leaveResponse.data || [];
      console.log('Raw leave data:', JSON.stringify(leaveData, null, 2));

      // Get employee details for gender
      const employeeData = await getEmployees({ employee_Id: employeeId });
      const employeeGenderId = employeeData[0]?.employee_Gender_Id;

      // Process the balance data
      const processedBalances = allLeaveTypes
        .filter(leaveType => {
          // Filter out leave types based on gender
          if (leaveType.value === 3) return employeeGenderId === 5; // Maternity Leave for women only
          if (leaveType.value === 4) return employeeGenderId === 1; // Paternity Leave for men only
          return true; // Show all other leave types
        })
        .map(leaveType => {
          // Find the balance record for this leave type
          const currentBalance = combinedBalances.find(b => b.leaveType_Id === leaveType.value);

          if (!currentBalance) {
            return null;
          }

          console.log(`Processing leave type ${leaveType.value} (${leaveType.label}):`, currentBalance);

          // Filter leaves for this type and calculate used days for the specific year
          const typeLeaves = leaveData.filter(leave => {
            const leaveTypeId = parseInt(leave.leave_LeaveType_Id || leave.Leave_LeaveType_Id, 10);
            const currentTypeId = parseInt(leaveType.value, 10);
            const isCancelled = leave.is_Cancelled || leave.Is_Cancelled;

            return leaveTypeId === currentTypeId && !isCancelled;
          });

          // Calculate PENDING used days for this specific year (applied but NOT approved yet)
          // IMPORTANT: In this system, approval happens when Rejoin Date is entered.
          // So "pending/applied" = no Rejoin Date, and "approved/posted" = has Rejoin Date.
          // Approved usage is taken from LeaveBalanceHistories (updated on approval), so we only add pending here.
          const pendingTypeLeaves = typeLeaves.filter(l => !(l.leave_RejoinDate || l.Leave_RejoinDate));
          const pendingUsedDays = pendingTypeLeaves.reduce((total, leave) => {
            if (leave.is_Cancelled || leave.Is_Cancelled) {
              return total;
            }

            const leaveFromYear = new Date(leave.leave_FromDate).getFullYear();
            const leaveToYear = new Date(leave.leave_ToDate).getFullYear();
            const isCrossYearLeave = leaveFromYear !== leaveToYear;

            if (isCrossYearLeave) {
              // For cross-year leaves, calculate days for this specific year
              const { fromYear: daysInFromYear, toYear: daysInToYear } = calculateDaysPerYear(
                leave.leave_FromDate,
                leave.leave_ToDate,
                leave.leave_LeaveType_Id
              );

              if (year === leaveFromYear) {
                return total + daysInFromYear;
              } else if (year === leaveToYear) {
                return total + daysInToYear;
              }
              return total; // Leave doesn't affect this year
            } else {
              // Single year leave - only count if it's in the current year
              if (leaveFromYear === year) {
                return total + (parseFloat(leave.leave_Days) || 0);
              }
              return total;
            }
          }, 0);

          console.log(`Balance calculation for ${leaveType.label} in year ${year}:`, {
            pendingUsedDays,
            pendingTypeLeaves: pendingTypeLeaves.map(l => ({
              leave_Id: l.leave_Id,
              fromDate: l.leave_FromDate,
              toDate: l.leave_ToDate,
              days: l.leave_Days,
              isCrossYear: new Date(l.leave_FromDate).getFullYear() !== new Date(l.leave_ToDate).getFullYear()
            }))
          });

          // Special handling for sick leave (type 2)
          const isSickLeave = leaveType.value === 2;
          let balance;

          if (isSickLeave) {
            const fullPaidLimit = currentBalance.total_FullPaidDays || 0;
            const halfPaidLimit = currentBalance.total_HalfPaidDays || 0;
            const unpaidLimit = currentBalance.total_UnpaidDays || 0;

            // Combined logic:
            // - Approved used: from LeaveBalanceHistories (updated on approval)
            // - Pending used: from EmployeeLeaves (applied but not approved yet)
            const approvedUsedFullPaid = parseFloat(currentBalance.used_FullPaidDays || 0);
            const approvedUsedHalfPaid = parseFloat(currentBalance.used_HalfPaidDays || 0);
            const approvedUsedUnpaid = parseFloat(currentBalance.used_UnpaidDays || 0);

            // Allocate pending days across remaining sick leave buckets (full -> half -> unpaid)
            let remainingPending = pendingUsedDays;
            const remainingFullCap = Math.max(0, fullPaidLimit - approvedUsedFullPaid);
            const pendingFullPaid = Math.min(remainingPending, remainingFullCap);
            remainingPending -= pendingFullPaid;

            const remainingHalfCap = Math.max(0, halfPaidLimit - approvedUsedHalfPaid);
            const pendingHalfPaid = Math.min(remainingPending, remainingHalfCap);
            remainingPending -= pendingHalfPaid;

            const remainingUnpaidCap = Math.max(0, unpaidLimit - approvedUsedUnpaid);
            const pendingUnpaid = Math.min(remainingPending, remainingUnpaidCap);

            const usedFullPaid = approvedUsedFullPaid + pendingFullPaid;
            const usedHalfPaid = approvedUsedHalfPaid + pendingHalfPaid;
            const usedUnpaid = approvedUsedUnpaid + pendingUnpaid;

            balance = {
              leaveType_Id: leaveType.value,
              leaveTypeName: leaveType.label,
              total_FullPaidDays: fullPaidLimit,
              total_HalfPaidDays: halfPaidLimit,
              total_UnpaidDays: unpaidLimit,
              used_FullPaidDays: usedFullPaid,
              used_HalfPaidDays: usedHalfPaid,
              used_UnpaidDays: usedUnpaid,
              available_FullPaidDays: fullPaidLimit - usedFullPaid,
              available_HalfPaidDays: halfPaidLimit - usedHalfPaid,
              available_UnpaidDays: unpaidLimit - usedUnpaid
            };
          } else {
            const totalAvailable = currentBalance.total_FullPaidDays || 0;
            const isUnpaidLeave = leaveType.value === 9;

            // Combined logic for ALL leave types:
            // - Approved used: from LeaveBalanceHistories (updated on approval)
            // - Pending used: from EmployeeLeaves (applied but not approved yet)
            const approvedUsed = parseFloat(currentBalance.used_FullPaidDays || 0);
            const displayedUsed = approvedUsed + pendingUsedDays;

            balance = {
              leaveType_Id: leaveType.value,
              leaveTypeName: leaveType.label,
              total_FullPaidDays: isUnpaidLeave ? 0 : totalAvailable,
              total_HalfPaidDays: currentBalance.total_HalfPaidDays || 0,
              total_UnpaidDays: currentBalance.total_UnpaidDays || 0,
              used_FullPaidDays: displayedUsed,
              used_HalfPaidDays: currentBalance.used_HalfPaidDays || 0,
              used_UnpaidDays: currentBalance.used_UnpaidDays || 0,
              available_FullPaidDays: isUnpaidLeave ? 0 : (totalAvailable - displayedUsed),
              available_HalfPaidDays: (currentBalance.total_HalfPaidDays || 0) - (currentBalance.used_HalfPaidDays || 0),
              available_UnpaidDays: (currentBalance.total_UnpaidDays || 0) - (currentBalance.used_UnpaidDays || 0)
            };
          }

          console.log(`Final balance for ${leaveType.label}:`, balance);
          return balance;
        }).filter(Boolean);

      setLeaveBalances(processedBalances);

    } catch (error) {
      console.error('Error loading leave balances:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to load leave balances',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const loadAvailableYears = async (employeeId) => {
    try {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      // Check if next year balances exist
      const nextYearBalances = await getLeaveBalanceHistory({
        Employee_Id: employeeId,
        BalanceYear: nextYear
      });

      if (nextYearBalances && nextYearBalances.length > 0) {
        setAvailableYears([currentYear, nextYear]);
      } else {
        setAvailableYears([currentYear]);
      }
    } catch (error) {
      console.error('Error loading available years:', error);
      setAvailableYears([new Date().getFullYear()]);
    }
  };

  const handleYearChange = async (selectedYearOption) => {
    if (selectedEmployee && selectedYearOption) {
      setSelectedYear(selectedYearOption.value);
      await loadLeaveBalances(selectedEmployee.value, selectedYearOption.value);
    }
  };

  const checkActiveLeave = async (employeeId) => {
    try {
      const response = await getEmployeeLeaves({ Leave_Employee_Id: employeeId });
      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to load leaves');
      }

      const leaves = response.data || [];
      // Find all active leaves that are not cancelled and not the current leave being edited
      const activeLeaves = leaves.filter(leave =>
        !leave.leave_RejoinDate &&
        !leave.Is_Cancelled && !leave.is_Cancelled &&
        leave.leave_Id !== form.Leave_Id // Don't show warning for the current leave being edited
      );

      if (activeLeaves.length > 0) {
        if (activeLeaves.length === 1) {
          const activeLeave = activeLeaves[0];
          const leaveType = leaveTypes.find(lt => lt.value === activeLeave.leave_LeaveType_Id);
          setActiveLeave({
            ...activeLeave,
            leaveTypeName: leaveType?.label || 'Unknown',
            multipleLeaves: false
          });
        } else {
          // Multiple active leaves found
          setActiveLeave({
            multipleLeaves: true,
            count: activeLeaves.length
          });
        }
      } else {
        setActiveLeave(null);
      }
    } catch (error) {
      console.error('Error checking active leave:', error);
    }
  };

  const LeaveBalanceTable = ({ balances }) => {
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRow = (leaveTypeId) => {
      if (expandedRow === leaveTypeId) {
        setExpandedRow(null);
      } else {
        setExpandedRow(leaveTypeId);
      }
    };

    return (
      <div className="mt-4">
        <h5>Leave Balances</h5>
        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th style={{ textAlign: "right", paddingRight: "40px" }}>Total Days</th>
                <th style={{ textAlign: "right", paddingRight: "40px" }}>Used Days</th>
                <th style={{ textAlign: "right", paddingRight: "40px" }}>Available Days</th>
              </tr>
            </thead>
            <tbody>
              {balances.map((balance, index) => {
                const isUnpaidLeave = balance.leaveType_Id === 9;
                const isSickLeave = balance.leaveType_Id === 2;

                return (
                  <React.Fragment key={index}>
                    <tr
                      className={isSickLeave ? "cursor-pointer" : ""}
                      onClick={() => isSickLeave && toggleRow(balance.leaveType_Id)}
                      style={{
                        backgroundColor: isSickLeave ? "#f8f9fa" : "transparent",
                        cursor: isSickLeave ? "pointer" : "default"
                      }}
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          {balance.leaveTypeName}
                          {isSickLeave && (
                            <i className={`ms-2 bx ${expandedRow === balance.leaveType_Id ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: "right", paddingRight: "40px" }}>
                        {isUnpaidLeave ? (
                          <span className="text-muted">Not Applicable</span>
                        ) : (
                          balance.total_FullPaidDays
                        )}
                      </td>
                      <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.used_FullPaidDays}</td>
                      <td style={{ textAlign: "right", paddingRight: "40px" }}>
                        {isUnpaidLeave ? (
                          <span className="text-muted">Not Applicable</span>
                        ) : (
                          balance.available_FullPaidDays
                        )}
                      </td>
                    </tr>
                    {isSickLeave && expandedRow === balance.leaveType_Id && (
                      <>
                        <tr className="bg-light">
                          <td style={{ paddingLeft: "40px" }}>Full Paid</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.total_FullPaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.used_FullPaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.available_FullPaidDays}</td>
                        </tr>
                        <tr className="bg-light">
                          <td style={{ paddingLeft: "40px" }}>Half Paid</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.total_HalfPaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.used_HalfPaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.available_HalfPaidDays}</td>
                        </tr>
                        <tr className="bg-light">
                          <td style={{ paddingLeft: "40px" }}>Unpaid</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.total_UnpaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.used_UnpaidDays}</td>
                          <td style={{ textAlign: "right", paddingRight: "40px" }}>{balance.available_UnpaidDays}</td>
                        </tr>
                        <tr className="bg-light">
                          <td colSpan="4" style={{ paddingLeft: "40px", fontSize: "12px", color: "#6c757d" }}>
                            <i className="bx bx-info-circle me-1"></i>
                            Sick leave structure: First {balance.total_FullPaidDays} days full pay,
                            next {balance.total_HalfPaidDays} days half pay,
                            followed by {balance.total_UnpaidDays} days unpaid if needed.
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const CreditHistoryTable = ({ credits }) => {
    // Ensure leave types are available
    useEffect(() => {
      if (leaveTypes.length === 0) {
        loadLeaveTypes();
      }
    }, []);

    const formatDate = (dateString) => {
      try {
        if (!dateString) return 'N/A';
        // Remove the time portion if it exists
        const datePart = dateString.split('T')[0];
        const date = new Date(datePart);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return format(date, 'dd/MM/yyyy');
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid Date';
      }
    };

    const getLeaveTypeName = (leaveTypeId) => {
      const leaveType = leaveTypes.find(lt => lt.value === leaveTypeId);
      return leaveType?.label || `Unknown (ID: ${leaveTypeId})`;
    };

    return (
      <div className="mt-4">
        <h5>Credit History</h5>
        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th>Credit Date</th>
                <th>Leave Type</th>
                <th>Credit Type</th>
                <th>Full Paid Days</th>
                <th>Half Paid Days</th>
                <th>Unpaid Days</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(credits) && credits.map((credit, index) => (
                <tr key={index}>
                  <td>{formatDate(credit.credit_Date)}</td>
                  <td>{getLeaveTypeName(credit.leaveType_Id)}</td>
                  <td>{credit.credit_Type || 'N/A'}</td>
                  <td>{credit.days_Credited_FullPay || 0}</td>
                  <td>{credit.days_Credited_HalfPay || 0}</td>
                  <td>{credit.days_Credited_NoPay || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleCancelLeave = async (leave) => {
    try {
      const result = await Swal.fire({
        title: 'Cancel Leave',
        text: 'Please provide a reason for cancelling this leave:',
        input: 'text',
        inputPlaceholder: 'Enter cancellation reason...',
        showCancelButton: true,
        confirmButtonText: 'Cancel Leave',
        cancelButtonText: 'Close',
        showLoaderOnConfirm: true,
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return 'You must enter a reason for cancellation!';
          }
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });
      if (result.isConfirmed) {
        const fromDate = new Date(leave.leave_FromDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        const totalDays = Math.max(0, Math.ceil((today - fromDate) / (1000 * 60 * 60 * 24)) + 1);
        let leaveDays = Math.max(0, totalDays - (Number(leave.offDays) || 0));
        if (leave.is_HalfDay || leave.Is_HalfDay) {
          if (totalDays === 1) {
            leaveDays = 0.5;
          } else {
            leaveDays = leaveDays - 0.5;
          }
        }
        leaveDays = Math.round(leaveDays * 100) / 100;
        const cancelledLeave = {
          ...leave,
          Is_Cancelled: true,
          Leave_CancelReason: result.value.trim(),
          Leave_CancelledBy: Number(localStorage.getItem("userId")) || 1,
          Leave_CancelledDate: new Date().toISOString(),
          Leave_Days: leaveDays
        };
        await saveEmployeeLeave(cancelledLeave);
        if (leaveDays > 0) {
          await updateLeaveBalance(leave);
        }
        Swal.fire({
          title: 'Success',
          text: 'Leave cancelled successfully',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true
        });
        await loadLeaves(filter);
        if (selectedEmployee) {
          await loadLeaveBalances(selectedEmployee.value);
        }
      }
    } catch (error) {
      console.error('Error cancelling leave:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to cancel leave',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    }
  };

  const loadCompensatoryLeaves = async () => {
    try {
      setLoading(true);
      const [leaves, employeesList] = await Promise.all([
        getCompensatoryLeaves(),
        getEmployees({})
      ]);

      // Map employee names to leaves
      const enrichedLeaves = leaves.map(leave => {
        const employee = employeesList.find(emp => emp.employee_Id === leave.employee_Id);
        return {
          ...leave,
          employeeName: employee ? (employee.employee_NameEn || employee.employee_Name) : 'Unknown'
        };
      });

      setCompensatoryLeaves(enrichedLeaves);
    } catch (error) {
      console.error("Error loading compensatory leaves:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load compensatory leaves",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompensatoryFormChange = (e) => {
    const { name, value } = e.target;
    setCompensatoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCompensatoryEmployeeChange = (selectedOption) => {
    setSelectedCompensatoryEmployee(selectedOption);
    setCompensatoryForm(prev => ({
      ...prev,
      Employee_Id: selectedOption ? selectedOption.value : null
    }));
  };

  const clearAllForms = () => {
    setForm(defaultForm);
    setSelectedEmployee(null);
    setSelectedEmployeeData(null);
    setSelectedLeaveType(null);
    setCompensatoryForm(defaultCompensatoryForm);
    setSelectedCompensatoryEmployee(null);
    setLeaveBalances([]);
    setCreditHistory([]);
    setShowCreditHistory(false);
    setSelectedYear(new Date().getFullYear());
    setAvailableYears([new Date().getFullYear()]);
  };

  const handleCompensatorySubmit = async (e) => {
    e.preventDefault();

    if (!compensatoryForm.Employee_Id || !compensatoryForm.CompensatoryDate) {
      Swal.fire({
        title: "Error!",
        text: "Please fill all required fields",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
      return;
    }

    try {
      setLoading(true);

      const balanceHistoryResponse = await getLeaveBalanceHistory({
        Employee_Id: compensatoryForm.Employee_Id,
        LeaveType_Id: 8,
        BalanceYear: new Date().getFullYear()
      });

      const compensatoryBalance = balanceHistoryResponse.find(b => b.leaveType_Id === 8);

      if (!compensatoryBalance) {
        throw new Error("Compensatory leave balance not found");
      }

      const saveData = {
        ...compensatoryForm,
        LeaveBalanceHistory_Id: compensatoryBalance.history_Id
      };

      await saveCompensatoryLeave(saveData);

      Swal.fire({
        title: "Success!",
        text: "Compensatory leave added successfully",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });

      // Reset both form and selected employee
      setCompensatoryForm(defaultCompensatoryForm);
      setSelectedCompensatoryEmployee(null);
      loadCompensatoryLeaves();
    } catch (error) {
      console.error("Error saving compensatory leave:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to save compensatory leave",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    clearAllForms();
    setActiveTab(tab);

    if (tab === "2") {
      loadLeaves(emptyFilter);
    } else if (tab === "3") {
      loadCompensatoryLeaves();
    }
  };

  // Add this new function to filter leaves based on category
  const getFilteredLeaves = () => {
    switch (leaveCategory) {
      case 'pending':
        return leaves.filter(leave =>
          !leave.leave_RejoinDate &&
          !leave.Is_Cancelled &&
          !leave.is_Cancelled
        );
      case 'past':
        return leaves.filter(leave =>
          leave.leave_RejoinDate &&
          !leave.Is_Cancelled &&
          !leave.is_Cancelled
        );
      case 'cancelled':
        return leaves.filter(leave =>
          leave.Is_Cancelled ||
          leave.is_Cancelled
        );
      default:
        return leaves;
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedLeaves = () => {
    const filteredLeaves = getFilteredLeaves();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredLeaves.slice(startIndex, endIndex);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0">Employee Leave Management</h4>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => handleTabClick("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Leave</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => handleTabClick("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Leave List</span>
                    </NavLink>
                  </NavItem>
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === "3" ? "bold" : "normal",
                          color: activeTab === "3" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => handleTabClick("3")}
                      >
                        <i className="bx bx-time-five"></i>
                        <span>Compensatory Leave</span>
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                {/* View Leave Modal */}
                <Modal isOpen={viewModalOpen} toggle={() => setViewModalOpen(false)} size="lg">
                  <ModalHeader toggle={() => setViewModalOpen(false)}>
                    Leave Details
                  </ModalHeader>
                  <ModalBody>
                    {selectedLeave && (
                      <div className="leave-details">
                        <Row className="mb-3">
                          <Col md={6}>
                            <p className="mb-2">
                              <strong>Employee:</strong> {selectedLeave.employeeName}
                            </p>
                            <p className="mb-2">
                              <strong>Leave Type:</strong> {selectedLeave.leaveTypeName}
                            </p>
                            <p className="mb-2">
                              <strong>Status:</strong>{" "}
                              <span className={`badge ${selectedLeave.is_Cancelled ? "bg-danger" :
                                selectedLeave.leave_RejoinDate ? "bg-success" :
                                  "bg-warning"
                                }`}>
                                {selectedLeave.is_Cancelled ? "Cancelled" :
                                  selectedLeave.leave_RejoinDate ? "Completed" :
                                    "Active"}
                              </span>
                            </p>
                          </Col>
                          <Col md={6}>
                            <p className="mb-2">
                              <strong>From Date:</strong>{" "}
                              {format(new Date(selectedLeave.leave_FromDate), 'dd/MM/yyyy')}
                            </p>
                            <p className="mb-2">
                              <strong>To Date:</strong>{" "}
                              {format(new Date(selectedLeave.leave_ToDate), 'dd/MM/yyyy')}
                            </p>
                            <p className="mb-2">
                              <strong>Days:</strong> {selectedLeave.leave_Days}
                            </p>
                            <p className="mb-2">
                              <strong>Off Days:</strong> {selectedLeave.offDays || 0}
                            </p>
                            <p className="mb-2">
                              <strong>Half Day:</strong> {selectedLeave.is_HalfDay ? "Yes" : "No"}
                            </p>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={12}>
                            <p className="mb-2">
                              <strong>Comments:</strong>
                            </p>
                            <div className="border rounded p-2">
                              {selectedLeave.leave_Comments || "No comments"}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <p className="mb-2">
                              <strong>Request Date:</strong>{" "}
                              {format(new Date(selectedLeave.leave_RequestDate), 'dd/MM/yyyy')}
                            </p>
                            {selectedLeave.leave_ApprovedDate && (
                              <p className="mb-2">
                                <strong>Approved Date:</strong>{" "}
                                {format(new Date(selectedLeave.leave_ApprovedDate), 'dd/MM/yyyy')}
                              </p>
                            )}
                          </Col>
                          <Col md={6}>
                            {selectedLeave.leave_RejoinDate && (
                              <p className="mb-2">
                                <strong>Rejoin Date:</strong>{" "}
                                {(() => {
                                  const rejoinDate = new Date(selectedLeave.leave_RejoinDate);
                                  rejoinDate.setDate(rejoinDate.getDate() + 1); // Add 1 day to show actual rejoin date
                                  return format(rejoinDate, 'dd/MM/yyyy');
                                })()}
                              </p>
                            )}
                          </Col>
                        </Row>
                      </div>
                    )}
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={() => setViewModalOpen(false)}>
                      Close
                    </Button>
                  </ModalFooter>
                </Modal>

                <TabContent activeTab={activeTab} className="p-4">
                  {hasEditOrHigher && (
                    <TabPane tabId="1">
                      <Form>
                        <Row>
                          <Col md={6}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Company *</Label>
                              <Select
                                value={selectedBusiness}
                                onChange={(option) => {
                                  handleBusinessChange(option);
                                  // reset employee selection for add form
                                  setSelectedEmployee(null);
                                  setForm(prev => ({ ...prev, Leave_Employee_Id: null }));
                                }}
                                options={businesses}
                                isClearable
                                isSearchable
                                placeholder="Select Company"
                                className="react-select"
                                classNamePrefix="select"
                                isDisabled={!!session.businessId}
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Branch</Label>
                              <Select
                                value={selectedBranch}
                                onChange={(option) => {
                                  handleBranchChange(option);
                                  // reset employee selection for add form
                                  setSelectedEmployee(null);
                                  setForm(prev => ({ ...prev, Leave_Employee_Id: null }));
                                }}
                                options={branches}
                                isClearable
                                isSearchable
                                placeholder="Select Branch"
                                className="react-select"
                                classNamePrefix="select"
                                isDisabled={!!session.branchId || !selectedBusiness}
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Employee *</Label>
                              <Select
                                value={form.Leave_Employee_Id ? employeeSearchOptions.find(opt => opt.value === form.Leave_Employee_Id) || selectedEmployee : null}
                                onChange={(option) => {
                                  handleSelectChange(option, "Leave_Employee_Id");
                                  if (option) {
                                    setEmployeeSearchOptions([]);
                                  }
                                }}
                                options={employeeSearchOptions}
                                isClearable={!form.Leave_Id}
                                isSearchable={!form.Leave_Id}
                                isDisabled={form.Leave_Id > 0}
                                isLoading={employeeSearchLoading}
                                filterOption={() => true}
                                noOptionsMessage={({ inputValue }) => {
                                  if (employeeSearchLoading) return "Searching...";
                                  if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                                  return "No employees found";
                                }}
                                loadingMessage={() => "Searching employees..."}
                                onInputChange={(inputValue, { action }) => {
                                  if (action === 'input-change' && !form.Leave_Id) {
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }
                                    const timeout = setTimeout(() => {
                                      searchEmployeesManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);
                                  }
                                  return inputValue;
                                }}
                                placeholder="Type at least 3 characters to search..."
                                className="react-select"
                                classNamePrefix="select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Leave Type *</Label>
                              <Select
                                value={selectedLeaveType}
                                onChange={(option) => handleSelectChange(option, "Leave_LeaveType_Id")}
                                options={leaveTypes}
                                isClearable={!form.Leave_Id}
                                isSearchable={!form.Leave_Id}
                                isDisabled={form.Leave_Id > 0}
                                placeholder="Select Leave Type"
                                className="react-select"
                                classNamePrefix="select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>From Date *</Label>
                              <Input
                                type="date"
                                name="Leave_FromDate"
                                value={form.Leave_FromDate || ""}
                                onChange={handleFormChange}
                                min={form.Leave_LeaveType_Id === 9 ? new Date().toISOString().split('T')[0] :
                                  (form.Leave_LeaveType_Id === 1 || form.Leave_LeaveType_Id === 2) && payrollPeriod ? payrollPeriod.startDate : undefined}
                                disabled={!form.Leave_Employee_Id || !form.Leave_LeaveType_Id}
                                required
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>To Date *</Label>
                              <Input
                                type="date"
                                name="Leave_ToDate"
                                value={form.Leave_ToDate || ""}
                                onChange={handleFormChange}
                                min={form.Leave_LeaveType_Id === 9 ?
                                  (form.Leave_FromDate || new Date().toISOString().split('T')[0]) :
                                  (form.Leave_FromDate || undefined)}
                                disabled={!form.Leave_Employee_Id || !form.Leave_LeaveType_Id || !form.Leave_FromDate}
                                required
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Off Days</Label>
                              <Input
                                type="number"
                                name="OffDays"
                                value={form.OffDays}
                                onChange={handleFormChange}
                                min="0"
                                max={form.Leave_FromDate && form.Leave_ToDate ?
                                  differenceInDays(new Date(form.Leave_ToDate), new Date(form.Leave_FromDate)) + 1 : 0}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Leave Days</Label>
                              <Input
                                type="number"
                                name="Leave_Days"
                                value={form.Leave_Days}
                                readOnly
                              />
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="mb-3">
                              <div className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="Is_HalfDay"
                                  name="Is_HalfDay"
                                  checked={form.Is_HalfDay}
                                  onChange={handleFormChange}
                                />
                                <Label className="form-check-label" for="Is_HalfDay">
                                  Half Day
                                </Label>
                              </div>
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="mb-3">
                              <Label>Comments</Label>
                              <Input
                                type="textarea"
                                name="Leave_Comments"
                                value={form.Leave_Comments || ""}
                                onChange={handleFormChange}
                                rows="3"
                              />
                            </div>
                          </Col>
                          {selectedEmployee && !isLoadingBalance && leaveBalances.length > 0 && (
                            <Col md={12}>
                              {/* Employee Information */}
                              {selectedEmployeeData && (
                                <div className="alert alert-info mb-4">
                                  <Row>
                                    <Col md={6}>
                                      <strong>Employee Code:</strong> {selectedEmployeeData.employee_Code}
                                    </Col>
                                    <Col md={6}>
                                      <strong>Join Date:</strong> {format(new Date(selectedEmployeeData.employee_JoinDate), 'dd/MM/yyyy')}
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {activeLeave && (
                                <div className="alert alert-warning mb-4">
                                  <strong>Active Leave Found!</strong><br />
                                  {activeLeave.multipleLeaves ? (
                                    <>This employee has {activeLeave.count} active leaves.</>
                                  ) : (
                                    <>This employee has an active leave from {format(new Date(activeLeave.leave_FromDate), 'dd/MM/yyyy')} to {format(new Date(activeLeave.leave_ToDate), 'dd/MM/yyyy')}.</>
                                  )}
                                </div>
                              )}
                              <div className="text-end mb-4">
                                {leaveBalances.length > 0 && hasEditOrHigher && (
                                  <Button color="primary" onClick={handleSubmit} disabled={loading}>
                                    {loading ? <ClipLoader size={20} color="#ffffff" /> : (form.Leave_Id ? "Update Leave" : "Save Leave")}
                                  </Button>
                                )}
                                {form.Leave_Id > 0 && (
                                  <Button
                                    color="secondary"
                                    className="ms-2"
                                    onClick={() => {
                                      setForm(defaultForm);
                                      setSelectedEmployee(null);
                                      setSelectedLeaveType(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>

                              {/* Select Year for Leave Balance View */}
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Leave Balances</h5>
                                <div className="d-flex align-items-center">
                                  <Label className="me-2 mb-0">Select Year:</Label>
                                  <Select
                                    value={{ value: selectedYear, label: selectedYear.toString() }}
                                    onChange={handleYearChange}
                                    options={availableYears.map(year => ({ value: year, label: year.toString() }))}
                                    isSearchable={false}
                                    placeholder="Select Year"
                                    className="react-select"
                                    classNamePrefix="select"
                                    styles={{
                                      menu: (provided) => ({
                                        ...provided,
                                        zIndex: 3
                                      })
                                    }}
                                  />
                                </div>
                              </div>
                              <LeaveBalanceTable balances={leaveBalances} />
                              <div className="d-flex justify-content-end mt-3 mb-3">
                                <Button
                                  color="primary"
                                  disabled={!selectedEmployee || isLoadingCredits}
                                  onClick={async () => {
                                    if (!showCreditHistory && selectedEmployee) {
                                      // Load credit history when showing it
                                      try {
                                        setIsLoadingCredits(true);
                                        const creditHistoryData = await getEmployeeCreditHistory(selectedEmployee.value);
                                        setCreditHistory(creditHistoryData);
                                      } catch (error) {
                                        console.error("Error loading credit history:", error);
                                        Swal.fire({
                                          title: "Error",
                                          text: "Failed to load credit history",
                                          icon: "error"
                                        });
                                      } finally {
                                        setIsLoadingCredits(false);
                                      }
                                    }
                                    setShowCreditHistory(!showCreditHistory);
                                  }}
                                >
                                  {isLoadingCredits ? (
                                    <>
                                      <ClipLoader size={16} color="#ffffff" className="me-2" />
                                      Loading...
                                    </>
                                  ) : (
                                    showCreditHistory ? "Hide Credit History" : "Show Credit History"
                                  )}
                                </Button>
                              </div>
                              {showCreditHistory && <CreditHistoryTable credits={creditHistory} />}
                            </Col>
                          )}
                        </Row>
                      </Form>
                    </TabPane>
                  )}

                  <TabPane tabId="2">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Button
                        color="primary"
                        className="btn btn-primary waves-effect waves-light"
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus me-1"></i> Add New Leave
                      </Button>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <Form className="mb-4">
                        <Row>
                          <Col md={3}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Company</Label>
                              <Select
                                value={selectedBusiness}
                                onChange={handleBusinessChange}
                                options={businesses}
                                isClearable
                                isSearchable
                                placeholder="Select Company"
                                className="react-select"
                                classNamePrefix="select"
                                isDisabled={!!session.businessId}
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Branch</Label>
                              <Select
                                value={selectedBranch}
                                onChange={handleBranchChange}
                                options={branches}
                                isClearable
                                isSearchable
                                placeholder="Select Branch"
                                className="react-select"
                                classNamePrefix="select"
                                isDisabled={!!session.branchId || !selectedBusiness}
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Employee</Label>
                              <Select
                                value={filter.Leave_Employee_Id ? employeeFilterSearchOptions.find(emp => emp.value === filter.Leave_Employee_Id) : null}
                                onChange={(option) => {
                                  setFilter(prev => ({ ...prev, Leave_Employee_Id: option?.value }));
                                  if (option) {
                                    setEmployeeFilterSearchOptions([]);
                                  }
                                }}
                                options={employeeFilterSearchOptions}
                                isClearable
                                isSearchable
                                isLoading={employeeFilterSearchLoading}
                                filterOption={() => true}
                                noOptionsMessage={({ inputValue }) => {
                                  if (employeeFilterSearchLoading) return "Searching...";
                                  if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                                  return "No employees found";
                                }}
                                loadingMessage={() => "Searching employees..."}
                                onInputChange={(inputValue, { action }) => {
                                  if (action === 'input-change') {
                                    if (filterSearchTimeout) {
                                      clearTimeout(filterSearchTimeout);
                                    }
                                    const timeout = setTimeout(() => {
                                      searchEmployeesFilterManually(inputValue);
                                    }, 300);
                                    setFilterSearchTimeout(timeout);
                                  }
                                  return inputValue;
                                }}
                                placeholder="Type at least 3 characters to search..."
                                className="react-select"
                                classNamePrefix="select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3" style={{ zIndex: 2 }}>
                              <Label>Leave Type</Label>
                              <Select
                                value={leaveTypes.find(lt => lt.value === filter.Leave_LeaveType_Id)}
                                onChange={(option) => setFilter(prev => ({ ...prev, Leave_LeaveType_Id: option?.value }))}
                                options={leaveTypes}
                                isClearable
                                isSearchable
                                placeholder="Select Leave Type"
                                className="react-select"
                                classNamePrefix="select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 3
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>From Date</Label>
                              <Input
                                type="date"
                                value={filter.Leave_FromDate || ""}
                                onChange={(e) => setFilter(prev => ({ ...prev, Leave_FromDate: e.target.value }))}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>To Date</Label>
                              <Input
                                type="date"
                                value={filter.Leave_ToDate || ""}
                                onChange={(e) => setFilter(prev => ({ ...prev, Leave_ToDate: e.target.value }))}
                              />
                            </div>
                          </Col>
                          <Col md={12} className="d-flex gap-2">
                            <Button color="primary" onClick={() => loadLeaves(filter)}>
                              Apply Filter
                            </Button>
                            <Button color="secondary" onClick={() => {
                              setFilter(emptyFilter);
                              loadLeaves(emptyFilter);
                            }}>
                              Clear Filter
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}

                    <div className="mb-4">
                      <Nav tabs className="nav-tabs-custom">
                        <NavItem>
                          <NavLink
                            className={leaveCategory === "pending" ? "active" : ""}
                            onClick={() => setLeaveCategory("pending")}
                            style={{ cursor: "pointer" }}
                          >
                            Pending Leaves
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={leaveCategory === "cancelled" ? "active" : ""}
                            onClick={() => setLeaveCategory("cancelled")}
                            style={{ cursor: "pointer" }}
                          >
                            Cancelled Leaves
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={leaveCategory === "past" ? "active" : ""}
                            onClick={() => setLeaveCategory("past")}
                            style={{ cursor: "pointer" }}
                          >
                            Past Leaves
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={leaveCategory === "all" ? "active" : ""}
                            onClick={() => setLeaveCategory("all")}
                            style={{ cursor: "pointer" }}
                          >
                            All Leaves
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </div>

                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Employee</th>
                            <th>Leave Type</th>
                            <th>From Date</th>
                            <th>To Date</th>
                            <th>Rejoin Date</th>
                            <th>Days</th>
                            <th>Off Days</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedLeaves().length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center">
                                No leaves found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedLeaves().map((leave, index) => (
                              <tr key={leave.leave_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{leave.employeeName}</td>
                                <td>{leave.leaveTypeName}</td>
                                <td>{leave.displayFromDate}</td>
                                <td>{leave.displayToDate}</td>
                                <td>
                                  {leave.leave_RejoinDate ? (() => {
                                    const rejoinDate = new Date(leave.leave_RejoinDate);
                                    rejoinDate.setDate(rejoinDate.getDate() + 1);
                                    return format(rejoinDate, 'dd/MM/yyyy');
                                  })() : '-'}
                                </td>
                                <td>{leave.leave_Days}</td>
                                <td>{leave.offDays || 0}</td>
                                <td>
                                  {leave.Is_Cancelled || leave.is_Cancelled ? (
                                    <span className="badge bg-danger">Cancelled</span>
                                  ) : leave.leave_RejoinDate || leave.Leave_RejoinDate ? (
                                    <span className="badge bg-success">Completed</span>
                                  ) : leave.leave_ApprovedDate || leave.Leave_ApprovedDate ? (
                                    (() => {
                                      const today = new Date();
                                      const fromDate = new Date(leave.leave_FromDate || leave.Leave_FromDate);
                                      if (fromDate > today) {
                                        return <span className="badge bg-info">Scheduled</span>;
                                      } else {
                                        return <span className="badge bg-warning">Active</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="badge bg-secondary">Pending</span>
                                  )}
                                </td>
                                <td>
                                  {(() => {
                                    const isEnded = leave.leave_RejoinDate || leave.Leave_RejoinDate;
                                    const isCancelled = leave.Is_Cancelled || leave.is_Cancelled;

                                    if (isEnded || isCancelled) {
                                      return (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleViewLeave(leave)}
                                          disabled={loading}
                                        >
                                          {loading ? <ClipLoader size={15} color="#ffffff" /> : "View"}
                                        </Button>
                                      );
                                    }

                                    return (
                                      <div className="d-flex gap-2">
                                        {hasEditOrHigher && (
                                          <>
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => handleCancelLeave(leave)}
                                              disabled={loading}
                                            >
                                              {loading ? <ClipLoader size={15} color="#ffffff" /> : "Cancel Leave"}
                                            </Button>
                                            {hasApprovePermission && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleRejoin(leave)}
                                                disabled={loading}
                                              >
                                                {loading ? <ClipLoader size={15} color="#ffffff" /> : "Set Rejoin Date"}
                                              </Button>
                                            )}
                                            <Button
                                              color="primary"
                                              size="sm"
                                              onClick={() => handleEdit(leave)}
                                              disabled={loading}
                                            >
                                              {loading ? <ClipLoader size={15} color="#ffffff" /> : "Edit"}
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Custom Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                      style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                      <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                        Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{getFilteredLeaves().length > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, getFilteredLeaves().length)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{getFilteredLeaves().length}</span> entries
                      </div>
                      {getFilteredLeaves().length > pageSize && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>

                          {[...Array(Math.ceil(getFilteredLeaves().length / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;

                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(getFilteredLeaves().length / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(getFilteredLeaves().length / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(getFilteredLeaves().length / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabPane>

                  {hasEditOrHigher && (
                    <TabPane tabId="3">
                      <Row>
                        <Col lg={12}>
                          <Card>
                            <CardBody>
                              <h4 className="card-title mb-4">Add Compensatory Leave</h4>
                              <Form id="compensatoryForm" onSubmit={handleCompensatorySubmit}>
                                <Row className="mb-4">
                                  <Col lg={6}>
                                    <FormGroup>
                                      <Label>Employee</Label>
                                      <Select
                                        value={selectedCompensatoryEmployee}
                                        onChange={handleCompensatoryEmployeeChange}
                                        options={employees}
                                        isClearable
                                        isSearchable
                                        placeholder="Select Employee"
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col lg={6}>
                                    <FormGroup>
                                      <Label>Date</Label>
                                      <Input
                                        type="date"
                                        name="CompensatoryDate"
                                        value={compensatoryForm.CompensatoryDate}
                                        onChange={handleCompensatoryFormChange}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <Row className="mt-4">
                                  <Col lg={12}>
                                    <FormGroup>
                                      <Label>Comments</Label>
                                      <Input
                                        type="textarea"
                                        name="Comments"
                                        value={compensatoryForm.Comments}
                                        onChange={handleCompensatoryFormChange}
                                        rows="3"
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    {hasEditOrHigher && (
                                      <div className="text-end mt-4">
                                        <Button type="submit" color="primary">
                                          {loading ? <ClipLoader size={20} color="#ffffff" /> : "Save"}
                                        </Button>
                                      </div>
                                    )}
                                  </Col>
                                </Row>
                              </Form>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={12}>
                          <Card>
                            <CardBody>
                              <h4 className="card-title mb-4">Compensatory Leave History</h4>
                              <div className="table-responsive">
                                <table className="table table-centered table-nowrap mb-0">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Employee</th>
                                      <th>Date</th>
                                      <th>Comments</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {compensatoryLeaves.map((leave, index) => (
                                      <tr key={leave.compensatory_Id}>
                                        <td>{index + 1}</td>
                                        <td>{leave.employeeName}</td>
                                        <td>{leave.compensatoryDate ? format(new Date(leave.compensatoryDate), 'dd/MM/yyyy') : '-'}</td>
                                        <td>{leave.comments}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </TabPane>
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EmployeeLeavePage; 